import $ from 'jquery';
import WebFont from 'webfontloader';
import constants, { LOGIN_PATH, MyMenusPath } from './constants';
import config from './config';
import designService from './service/designService';
import imageService from "./service/imageService";
import store from './store';
import * as actions from "./actions";
import Renderer from "./canvas/renderer";
import colorService from './service/colorService';
import { fitToPage } from "./canvas/zoom";
import MenuObject, { overwriteStyles } from "./canvas/menu-object";
import { getDesignFonts } from "./canvas/font";
import frontendLogService from './service/frontendLogService';
import Interactable from './canvas/interactable';

class DesignSize {
    constructor(data) {
        this.key = data.key;
        this.label = data.label;
        this.width = data.width + (data.bleed || 0);
        this.height = data.height + (data.bleed || 0);
        this.unit = data.unit;
        this.category = data.category;
        this.detailedCategory = data.detailedCategory;
        this.selected = data.selected;
        this.thumb = data.thumb;
        this.thumbWidth = data.thumbWidth;
        this.ratio = ((data.width + (data.bleed || 0)) / (data.height + (data.bleed || 0)) * 100) / 100;
        this.detailedLabel = data.detailedLabel || '';
        this.bleed = data.bleed || 0;
        this.widthNoBleed = data.width;
        this.heightNoBleed = data.height;
        this.sizeLabel = data.sizeLabel;
        this.folds = data.folds;
        this.dimensionsLabel = this.widthNoBleed + ' x ' + this.heightNoBleed + ' ' + this.unit;
        this.isCircular = data.isCircular;
    }
}

const flyerSizes = ['letter', 'legal', 'legal-landscape', 'tabloid', 'a4', 'letter-landscape', 'tabloid-landscape', 'a4-landscape'];
const bizSizes = ['biz-card-landscape', 'biz-card-portrait', 'biz-card-square', 'biz-card-circular'];
const menuSizes = ['menu-letter', 'menu-letter-landscape', 'menu-legal', 'menu-legal-landscape', 'menu-tabloid', 'menu-tabloid-landscape',
    'menu-a4', 'menu-a4-landscape'];
const loyaltyCardSizes = ['loyalty-card-landscape', 'loyalty-card-portrait'];
const kidsMenuSizes = ['kids-menu-letter-portrait', 'kids-menu-letter-landscape', 'kids-menu-legal-portrait', 'kids-menu-legal-landscape',
    'kids-menu-tabloid-portrait', 'kids-menu-tabloid-landscape', 'kids-menu-a4-portrait', 'kids-menu-a4-landscape'];
const commentCardSizes = ['comment-card-portrait'];
const rackCardSizes = ['rack-card-portrait'];
const giftCertificateSizes = ['gift-certificate-landscape'];
const takeoutSizes = ['takeout-letter-trifold', 'takeout-letter-bifold', 'takeout-legal-trifold', 'takeout-legal-quadfold', 'takeout-tabloid-trifold'];
const foldedMenuSizes = ['folded-menu-letter-trifold', 'folded-menu-letter-bifold', 'folded-menu-legal-trifold', 'folded-menu-tabloid-trifold'];
const halfPageMenuSizes = ['half-page-menu', 'half-page-menu-long', 'half-page-menu-wide', 'half-page-menu-wide-landscape'];
const tableTentSizes = ['table-tent'];
const tabletopInsertSizes = ['table-tent-insert', 'table-tent-insert-4x6'];
const posterSizes = ['poster'];
const digitalMenuBoardSizes = ['digital-menu-board', 'digital-menu-board-portrait'];
const menuMailerSizes = ['menu-mailer-tabloid-trifold'];
const stickerSizes = ['sticker-landscape', 'sticker-portrait', 'sticker-square'];
const labelSizes = ['label-landscape', 'label-portrait', 'label-square'];
const sandwichBoardSizes = ['sandwich-board'];
const logoSizes = ['logo'];

const bleed = config.bleedByDefault ? 0.25 : 0;
const doubleBleed = config.bleedByDefault ? 0.5 : 0;
const halfBleed = config.bleedByDefault ? 0.125 : 0;
const isTestEnv = location.hostname.match('test.mhmfun.com');

const sizes = [
    new DesignSize({
        key: 'custom',
        label: 'Custom',
        category: 'custom',
        detailedLabel: 'Custom',
        width: '',
        height: '',
        unit: '',
        selected: false,
        bleed: '',
        sizeLabel: 'Custom',
    }),

    new DesignSize({
        key: 'menu-letter',
        label: 'Menu',
        category: 'menus',
        detailedLabel: 'Menu',
        width: 8.5,
        height: 11,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Letter',
    }),
    new DesignSize({
        key: 'menu-legal',
        label: 'Menu',
        category: 'menus',
        width: 8.5,
        height: 14,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Legal',
    }),
    new DesignSize({
        key: 'menu-tabloid',
        label: 'Menu',
        category: 'menus',
        detailedLabel: 'Menu',
        width: 11,
        height: 17,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Tabloid',
    }),
    new DesignSize({
        key: 'menu-a4',
        label: 'Menu',
        category: 'menus',
        width: 8.27,
        height: 11.69,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'A4',
    }),
    new DesignSize({
        key: 'menu-letter-landscape',
        label: 'Menu',
        category: 'menus',
        detailedLabel: 'Menu',
        width: 11,
        height: 8.5,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Letter Landscape',
    }),
    new DesignSize({
        key: 'menu-legal-landscape',
        label: 'Menu',
        category: 'menus',
        width: 14,
        height: 8.5,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Legal Landscape',
    }),
    new DesignSize({
        key: 'menu-tabloid-landscape',
        label: 'Menu',
        category: 'menus',
        detailedLabel: 'Menu',
        width: 17,
        height: 11,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Tabloid Landscape',
    }),
    new DesignSize({
        key: 'menu-a4-landscape',
        label: 'Menu',
        category: 'menus',
        width: 11.69,
        height: 8.27,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'A4 Landscape',
    }),

    new DesignSize({
        key: 'digital-menu-board',
        label: 'Digital Menu Board',
        detailedLabel: 'Digital Menu Board',
        width: 16,
        height: 9,
        unit: 'in',
        category: 'digital-menu-boards',
        selected: false,
        sizeLabel: '',
    }),
    new DesignSize({
        key: 'digital-menu-board-portrait',
        label: 'Digital Menu Board',
        detailedLabel: 'Portrait Digital Menu Board',
        width: 9,
        height: 16,
        unit: 'in',
        category: 'digital-menu-boards',
        selected: false,
        sizeLabel: '',
    }),

    new DesignSize({
        key: 'kids-menu-letter-portrait',
        label: 'Kids Menu',
        category: 'kids-menus',
        detailedLabel: 'Kids Menu',
        width: 8.5,
        height: 11,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Letter',
    }),
    new DesignSize({
        key: 'kids-menu-legal-portrait',
        label: 'Kids Menu',
        category: 'kids-menus',
        detailedLabel: 'Kids Menu',
        width: 8.5,
        height: 14,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Legal',
    }),
    new DesignSize({
        key: 'kids-menu-tabloid-portrait',
        label: 'Kids Menu',
        category: 'kids-menus',
        detailedLabel: 'Kids Menu',
        width: 11,
        height: 17,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Tabloid',
    }),
    new DesignSize({
        key: 'kids-menu-a4-portrait',
        label: 'Kids Menu',
        category: 'kids-menus',
        width: 8.27,
        height: 11.69,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'A4',
    }),
    new DesignSize({
        key: 'kids-menu-letter-landscape',
        label: 'Kids Menu',
        category: 'kids-menus',
        detailedLabel: 'Kids Menu',
        width: 11,
        height: 8.5,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Landscape',
    }),
    new DesignSize({
        key: 'kids-menu-legal-landscape',
        label: 'Kids Menu',
        category: 'kids-menus',
        detailedLabel: 'Kids Menu',
        width: 14,
        height: 8.5,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Landscape',
    }),
    new DesignSize({
        key: 'kids-menu-tabloid-landscape',
        label: 'Kids Menu',
        category: 'kids-menus',
        detailedLabel: 'Kids Menu',
        width: 17,
        height: 11,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Landscape',
    }),
    new DesignSize({
        key: 'kids-menu-a4-landscape',
        label: 'Kids Menu',
        category: 'kids-menus',
        width: 11.69,
        height: 8.27,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Landscape',
    }),

    new DesignSize({
        key: 'table-tent',
        label: 'Table Tent',
        detailedLabel: 'Table Tent',
        width: 4,
        height: 6,
        unit: 'in',
        category: 'table-tents',
        selected: false,
        bleed,
        sizeLabel: '',
    }),
    new DesignSize({
        key: 'table-tent-insert',
        label: 'Tabletop Insert',
        detailedLabel: 'Tabletop Insert',
        width: 5,
        height: 7,
        unit: 'in',
        category: 'table-tents',
        detailedCategory: 'table-tent-inserts',
        selected: false,
        bleed,
        sizeLabel: '',
    }),
    new DesignSize({
        key: 'table-tent-insert-4x6',
        label: 'Tabletop Insert',
        detailedLabel: 'Table Tent Insert',
        width: 4,
        height: 6,
        unit: 'in',
        category: 'table-tents',
        selected: false,
        bleed,
        sizeLabel: '',
    }),

    new DesignSize({
        key: 'half-page-menu',
        label: 'Half Page',
        detailedLabel: 'Half Page',
        width: 4.25,
        height: 11,
        unit: 'in',
        category: 'half-page-menus',
        selected: false,
        bleed,
        sizeLabel: '',
    }),
    new DesignSize({
        key: 'half-page-menu-long',
        label: 'Half Page',
        detailedLabel: 'Half Page',
        width: 4.25,
        height: 14,
        unit: 'in',
        category: 'half-page-menus',
        selected: false,
        bleed,
        sizeLabel: 'Long',
    }),
    new DesignSize({
        key: 'half-page-menu-wide',
        label: 'Half Page',
        detailedLabel: 'Half Page',
        width: 5.5,
        height: 8.5,
        unit: 'in',
        category: 'half-page-menus',
        selected: false,
        bleed,
        sizeLabel: 'Wide',
    }),
    new DesignSize({
        key: 'half-page-menu-wide-landscape',
        label: 'Half Page',
        detailedLabel: 'Half Page',
        width: 8.5,
        height: 5.5,
        unit: 'in',
        category: 'half-page-menus',
        selected: false,
        bleed,
        sizeLabel: 'Landscape',
    }),

    new DesignSize({
        key: 'takeout-letter-trifold',
        label: 'Takeout',
        detailedLabel: 'Takeout',
        width: 11.0,
        height: 8.5,
        unit: 'in',
        category: 'takeouts',
        selected: false,
        bleed,
        sizeLabel: 'Letter Trifold',
        folds: [
            {
                foldOne: 3.7438,
                foldTwo: 7.425,
                labelOne: 'outer panel',
                labelTwo: 'back cover',
                labelThree: 'front cover'
            },
            {
                foldOne: 3.825,
                foldTwo: 7.50,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3'
            }
        ]
    }),

    new DesignSize({
        key: 'takeout-letter-bifold',
        label: 'Takeout',
        detailedLabel: 'Takeout',
        width: 11.0,
        height: 8.5,
        unit: 'in',
        category: 'takeouts',
        selected: false,
        bleed,
        sizeLabel: 'Letter Bifold',
        folds: [
            {
                foldOne: 5.6254,
                labelOne: 'outer panel',
                labelTwo: 'front cover',
            },
            {
                foldOne: 5.6254,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
            }
        ]
    }),

    new DesignSize({
        key: 'takeout-legal-trifold',
        label: 'Takeout',
        detailedLabel: 'Takeout',
        width: 14.0,
        height: 8.5,
        unit: 'in',
        category: 'takeouts',
        selected: false,
        bleed,
        sizeLabel: 'Legal Trifold',
        folds: [
            {
                foldOne: 4.7438,
                foldTwo: 9.425,
                labelOne: 'outer panel',
                labelTwo: 'back cover',
                labelThree: 'front cover'
            },
            {
                foldOne: 4.825,
                foldTwo: 9.50,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3'
            }
        ]
    }),

    new DesignSize({
        key: 'takeout-legal-quadfold',
        label: 'Takeout',
        detailedLabel: 'Takeout',
        width: 14.0,
        height: 8.5,
        unit: 'in',
        category: 'takeouts',
        selected: false,
        bleed,
        sizeLabel: 'Legal Quadfold',
        folds: [
            {
                foldOne: 3.616,
                foldTwo: 7.1042,
                foldThree: 10.5974,
                labelOne: 'outer panel 1',
                labelTwo: 'outer panel 2',
                labelThree: 'back cover',
                labelFour: 'front cover'
            },
            {
                foldOne: 3.64,
                foldTwo: 7.1458,
                foldThree: 10.634,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3',
                labelFour: 'inside panel 4'
            },
        ]
    }),

    new DesignSize({
        key: 'takeout-tabloid-trifold',
        label: 'Takeout',
        detailedLabel: 'Takeout',
        width: 17.0,
        height: 11.0,
        unit: 'in',
        category: 'takeouts',
        selected: false,
        bleed,
        sizeLabel: 'Tabloid Trifold',
        folds: [
            {
                foldOne: 5.76,
                foldTwo: 11.4,
                labelOne: 'outer panel',
                labelTwo: 'back cover',
                labelThree: 'front cover'
            },
            {
                foldOne: 5.844,
                foldTwo: 11.5,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3'
            }
        ]
    }),

    new DesignSize({
        key: 'letter',
        label: 'Flyer',
        category: 'flyers',
        detailedLabel: 'Flyer',
        width: 8.5,
        height: 11,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Letter',
    }),
    new DesignSize({
        key: 'legal',
        label: 'Flyer',
        category: 'flyers',
        detailedLabel: 'Flyer',
        width: 8.5,
        height: 14,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Legal',
    }),
    new DesignSize({
        key: 'tabloid',
        label: 'Flyer',
        category: 'flyers',
        detailedLabel: 'Flyer',
        width: 11,
        height: 17,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Tabloid',
    }),
    new DesignSize({
        key: 'a4',
        label: 'Flyer',
        category: 'flyers',
        width: 8.27,
        height: 11.69,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'A4',
    }),
    new DesignSize({
        key: 'letter-landscape',
        label: 'Flyer',
        category: 'flyers',
        detailedLabel: 'Flyer',
        width: 11,
        height: 8.5,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Letter Landscape',
    }),
    new DesignSize({
        key: 'legal-landscape',
        label: 'Flyer',
        category: 'flyers',
        detailedLabel: 'Flyer',
        width: 14,
        height: 8.5,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Legal Landscape',
    }),
    new DesignSize({
        key: 'tabloid-landscape',
        label: 'Flyer',
        category: 'flyers',
        detailedLabel: 'Flyer',
        width: 17,
        height: 11,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'Tabloid Landscape',
    }),
    new DesignSize({
        key: 'a4-landscape',
        label: 'Flyer',
        category: 'flyers',
        width: 11.69,
        height: 8.27,
        unit: 'in',
        selected: false,
        bleed,
        sizeLabel: 'A4 Landscape',
    }),

    new DesignSize({
        key: 'biz-card-landscape',
        label: 'Business Card',
        detailedLabel: 'Business Card',
        width: 3.5,
        height: 2,
        unit: 'in',
        category: 'business-cards',
        selected: false,
        bleed,
        sizeLabel: 'Standard',
    }),
    new DesignSize({
        key: 'biz-card-portrait',
        label: 'Business Card',
        width: 2,
        height: 3.5,
        unit: 'in',
        category: 'business-cards',
        selected: false,
        bleed,
        sizeLabel: 'Portrait',
    }),
    new DesignSize({
        key: 'biz-card-square',
        label: 'Business Card',
        width: 2.5,
        height: 2.5,
        unit: 'in',
        category: 'business-cards',
        selected: false,
        bleed,
        sizeLabel: 'Square',
    }),
    new DesignSize({
        key: 'biz-card-circular',
        label: 'Business Card',
        detailedLabel: 'Circular Business Card',
        width: 2.5,
        height: 2.5,
        unit: 'in',
        category: 'business-cards',
        selected: false,
        bleed: halfBleed,
        sizeLabel: 'Circular',
        isCircular: true,
    }),


    new DesignSize({
        key: 'fb-post',
        label: 'Facebook Post',
        detailedLabel: 'Facebook Post',
        width: 1200,
        height: 628,
        unit: 'px',
        category: 'social',
        detailedCategory: 'facebook',
        selected: false,
        sizeLabel: '',
    }),

    new DesignSize({
        key: 'fb-story',
        label: 'Facebook Story',
        detailedLabel: 'Facebook Story',
        width: 1080,
        height: 1920,
        unit: 'px',
        category: 'social',
        detailedCategory: 'facebook',
        selected: false,
        sizeLabel: '',
    }),

    new DesignSize({
        key: 'insta-post',
        label: 'Instagram Post',
        detailedLabel: 'Instagram Post',
        width: 1080,
        height: 1080,
        unit: 'px',
        category: 'social',
        detailedCategory: 'instagram',
        selected: false,
        sizeLabel: '',
    }),

    new DesignSize({
        key: 'insta-story',
        label: 'Instagram Story',
        detailedLabel: 'Instagram Story',
        width: 1080,
        height: 1920,
        unit: 'px',
        category: 'social',
        detailedCategory: 'instagram',
        selected: false,
        sizeLabel: '',
    }),


    new DesignSize({
        key: 'loyalty-card-landscape',
        label: 'Loyalty Card',
        detailedLabel: 'Loyalty Card',
        width: 3.5,
        height: 2,
        unit: 'in',
        category: 'loyalty-cards',
        selected: false,
        bleed,
        sizeLabel: 'Standard',
    }),
    new DesignSize({
        key: 'loyalty-card-portrait',
        label: 'Loyalty Card',
        detailedLabel: 'Loyalty Card',
        width: 2,
        height: 3.5,
        unit: 'in',
        category: 'loyalty-cards',
        selected: false,
        bleed,
        sizeLabel: 'Portrait',
    }),

    new DesignSize({
        key: 'comment-card-portrait',
        label: 'Comment Card',
        detailedLabel: 'Comment Card',
        width: 3.5,
        height: 8.5,
        unit: 'in',
        category: 'comment-cards',
        selected: false,
        bleed,
        sizeLabel: 'Portrait',
    }),
    new DesignSize({
        key: 'rack-card-portrait',
        label: 'Rack Card',
        detailedLabel: 'Rack Card',
        width: 3.5,
        height: 8.5,
        unit: 'in',
        category: 'rack-cards',
        selected: false,
        bleed,
        sizeLabel: 'Portrait',
    }),
    new DesignSize({
        key: 'gift-certificate-landscape',
        label: 'Gift Certificate',
        detailedLabel: 'Gift Certificate',
        width: 8.5,
        height: 3.5,
        unit: 'in',
        category: 'gift-certificates',
        selected: false,
        bleed,
        sizeLabel: '',
    }),

    new DesignSize({
        key: 'folded-menu-letter-trifold',
        label: 'Folded Menu',
        detailedLabel: 'Folded Dine-in Menu',
        width: 11.0,
        height: 8.5,
        unit: 'in',
        category: 'folded-menus',
        selected: false,
        bleed,
        sizeLabel: 'Trifold',
        folds: [
            {
                foldOne: 3.7438,
                foldTwo: 7.425,
                labelOne: 'outer panel',
                labelTwo: 'back cover',
                labelThree: 'front cover'
            },
            {
                foldOne: 3.825,
                foldTwo: 7.50,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3'
            }
        ]
    }),
    new DesignSize({
        key: 'folded-menu-letter-bifold',
        label: 'Folded Menu',
        detailedLabel: 'Folded Dine-in Menu',
        width: 11.0,
        height: 8.5,
        unit: 'in',
        category: 'folded-menus',
        selected: false,
        bleed,
        sizeLabel: 'Bifold',
        folds: [
            {
                foldOne: 5.6254,
                labelOne: 'outer panel',
                labelTwo: 'front cover',
            },
            {
                foldOne: 5.6254,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
            }
        ]
    }),
    new DesignSize({
        key: 'folded-menu-legal-trifold',
        label: 'Folded Menu',
        detailedLabel: 'Folded Dine-in Menu',
        width: 14.0,
        height: 8.5,
        unit: 'in',
        category: 'folded-menus',
        selected: false,
        bleed,
        sizeLabel: 'Trifold',
        folds: [
            {
                foldOne: 4.7438,
                foldTwo: 9.425,
                labelOne: 'outer panel',
                labelTwo: 'back cover',
                labelThree: 'front cover'
            },
            {
                foldOne: 4.825,
                foldTwo: 9.50,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3'
            }
        ]
    }),
    new DesignSize({
        key: 'folded-menu-tabloid-trifold',
        label: 'Folded Menu',
        detailedLabel: 'Folded Dine-in Menu',
        width: 17.0,
        height: 11.0,
        unit: 'in',
        category: 'folded-menus',
        selected: false,
        bleed,
        sizeLabel: 'Trifold',
        folds: [
            {
                foldOne: 5.76,
                foldTwo: 11.4,
                labelOne: 'outer panel',
                labelTwo: 'back cover',
                labelThree: 'front cover'
            },
            {
                foldOne: 5.844,
                foldTwo: 11.5,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3'
            }
        ]
    }),

    new DesignSize({
        key: 'poster',
        label: 'Poster',
        detailedLabel: 'Poster',
        width: 22,
        height: 28,
        unit: 'in',
        category: 'posters',
        selected: false,
        bleed,
        sizeLabel: '',
    }),
    new DesignSize({
        key: 'menu-mailer-tabloid-trifold',
        label: 'Menu Mailer',
        detailedLabel: 'Menu Mailer',
        width: 17.0,
        height: 11.0,
        unit: 'in',
        category: 'menu-mailers',
        selected: false,
        bleed,
        sizeLabel: 'Trifold',
        folds: [
            {
                foldOne: 5.76,
                foldTwo: 11.4,
                labelOne: 'outer panel',
                labelTwo: 'back cover',
                labelThree: 'front cover'
            },
            {
                foldOne: 5.844,
                foldTwo: 11.5,
                labelOne: 'inside panel 1',
                labelTwo: 'inside panel 2',
                labelThree: 'inside panel 3'
            }
        ]
    }),

    new DesignSize({
        key: 'sticker-landscape',
        label: 'Sticker',
        detailedLabel: 'Sticker',
        width: 3.5,
        height: 2,
        unit: 'in',
        category: 'stickers',
        selected: false,
        bleed: halfBleed,
        sizeLabel: 'Landscape',
    }),
    new DesignSize({
        key: 'sticker-portrait',
        label: 'Sticker',
        detailedLabel: 'Sticker',
        width: 2,
        height: 3.5,
        unit: 'in',
        category: 'stickers',
        selected: false,
        bleed: halfBleed,
        sizeLabel: 'Portrait',
    }),
    new DesignSize({
        key: 'sticker-square',
        label: 'Sticker',
        detailedLabel: 'Sticker',
        width: 2.5,
        height: 2.5,
        unit: 'in',
        category: 'stickers',
        selected: false,
        bleed: halfBleed,
        sizeLabel: 'Square',
    }),

    new DesignSize({
        key: 'label-landscape',
        label: 'Label',
        detailedLabel: 'Food Label',
        width: 3.5,
        height: 2,
        unit: 'in',
        category: 'labels',
        selected: false,
        bleed: halfBleed,
        sizeLabel: 'Landscape',
    }),
    new DesignSize({
        key: 'label-portrait',
        label: 'Label',
        detailedLabel: 'Label',
        width: 2,
        height: 3.5,
        unit: 'in',
        category: 'labels',
        selected: false,
        bleed: halfBleed,
        sizeLabel: 'Portrait',
    }),
    new DesignSize({
        key: 'label-square',
        label: 'Label',
        detailedLabel: 'Label',
        width: 2.5,
        height: 2.5,
        unit: 'in',
        category: 'labels',
        selected: false,
        bleed: halfBleed,
        sizeLabel: 'Square',
    }),


    new DesignSize({
        key: 'sandwich-board',
        label: 'Sandwich Board',
        detailedLabel: 'Sandwich Board',
        width: 22,
        height: 28,
        unit: 'in',
        category: 'sandwich-boards',
        selected: false,
        bleed: doubleBleed,
        sizeLabel: '',
    }),

    new DesignSize({
        key: 'logo',
        label: 'Logo',
        detailedLabel: 'Logo',
        width: 1000,
        height: 1000,
        unit: 'px',
        category: 'logos',
        selected: false,
        sizeLabel: '',
    }),
];

const getPrintCartSizeLabel = (size) => {
    if (takeoutSizes.indexOf(size) > -1) {
        return 'Folded Takeout';
    } else {
        return getSizeLabel(size);
    }
}

const getSizeLabel = (size) => {
    if (flyerSizes.indexOf(size) > -1) return 'Flyer';
    if (bizSizes.indexOf(size) > -1) return 'Business Card';
    if (size === 'fb-post') return 'Facebook Post';
    if (size === 'fb-story') return 'Facebook Story';
    if (size === 'insta-post') return 'Instagram Post';
    if (size === 'insta-story') return 'Instagram Story';
    if (menuSizes.indexOf(size) > -1) return 'Menu';
    if (loyaltyCardSizes.indexOf(size) > -1) return 'Loyalty Card';
    if (kidsMenuSizes.indexOf(size) > -1) return 'Kids Menu';
    if (commentCardSizes.indexOf(size) > -1) return 'Comment Card';
    if (rackCardSizes.indexOf(size) > -1) return 'Rack Card';
    if (giftCertificateSizes.indexOf(size) > -1) return 'Gift Certificate';
    if (takeoutSizes.indexOf(size) > -1) return 'Takeout';
    if (foldedMenuSizes.indexOf(size) > -1) return 'Folded Menu';
    if (halfPageMenuSizes.indexOf(size) > -1) return 'Half Page';
    if (tableTentSizes.indexOf(size) > -1) return 'Table Tent';
    if (tabletopInsertSizes.indexOf(size) > -1) return 'Tabletop Insert';
    if (posterSizes.indexOf(size) > -1) return 'Poster';
    if (digitalMenuBoardSizes.indexOf(size) > -1) return 'Digital Menu Board';
    if (menuMailerSizes.indexOf(size) > -1) return 'Menu Mailer';
    if (stickerSizes.indexOf(size) > -1) return 'Sticker';
    if (labelSizes.indexOf(size) > -1) return 'Label';
    if (sandwichBoardSizes.indexOf(size) > -1) return 'Sandwich Board';
    if (logoSizes.indexOf(size) > -1) return 'Logo';

    return size
};

const getSize = size => sizes.find((_) => _.key === size);

const getSizeWithLabel = (size) => {
    const found = (typeof size === 'object') ? size : getSize(size)
    return found.label + " " + found.sizeLabel
}

const getSizeDimensionsLabel = (size) => {
    const found = getSize(size)
    return found.dimensionsLabel
}

const getFont = function (key) {
    let font = constants.fonts.find((font) => font.key === key)
    if (!font) font = constants.multiLangFonts.find((font) => font.key === key)
    if (!font) font = constants.customFonts.find((font) => font.key === key)
    return font
}

const getFontByFamily = function (family) {
    let font = constants.fonts.find((font) => font.font === family)
    if (!font) font = constants.multiLangFonts.find((font) => font.font === family)
    if (!font) font = constants.customFonts.find((font) => font.font === family)
    return font
}

const getFontByKey = function (key) {
    let font = constants.fonts.find((font) => font.key === key)
    if (!font) font = constants.multiLangFonts.find((font) => font.key === key)
    if (!font) font = constants.customFonts.find((font) => font.key === key)
    return font
}

const getFontByLabel = function (label) {
    let font = constants.fonts.find((font) => font.font === label)
    if (!font) font = constants.multiLangFonts.find((font) => font.font === label)
    if (!font) font = constants.customFonts.find((font) => font.font === label)
    return font
}

const UUID = new function () {
    const length = 12;
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const charactersLength = characters.length;
    this.get = function () {
        let result = '';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }
}();

function radToDegree(rad) {
    return parseInt(rad * (180 / Math.PI), 10);
}

const contextBarHeight = 72;

function hideDropdown(ev) {
    if (!$('.search-fonts').is(':focus') || !$('.search-fonts').hasClass('focus-visible') || store.getState().view !== 'mo-edit') {
        if ((store.getState().isMobileVersion && !ev) || (!$('.search-fonts').hasClass('focus-visible') && (store.getState().view !== 'mo-edit'))) {
            $('.dropdown:not(.nested)').removeClass('show');
            $('.dropdown-menu:not(.nested)').removeClass('show');
            $('.suggested-categories').hide();

            if (store.getState().isMobileVersion) {
                resetMobileContextBar();
            }
        } else if (store.getState().view === 'text-mo-edit' && ev) {
            if ($(ev.target).hasClass('field') || $(ev.target).hasClass('item-details')) {
                $('.dropdown:not(.nested)').removeClass('show');
                $('.dropdown-menu:not(.nested)').removeClass('show');

                if (store.getState().isMobileVersion) {
                    resetMobileContextBar();
                }
            }
        }
    }
}

export function resetMobileContextBar() {
    $('.context-bar').css('height', contextBarHeight + 'px');
    $('#context-bar .close-context-bar').show();
}

const initDropdown = function () {
    const $document = $(document);

    $document.on('click', 'body, .page', hideDropdown);

    function init(ev, _this) {
        ev.stopPropagation();

        const $this = $(_this);
        if ($this.hasClass('disabled')) return;

        /*if ($this.hasClass('download') || $this.hasClass('publish-first') || $this.hasClass('publish-changes-drop') || $this.hasClass('publish-changes')) {
            if ((store.getState().hasVideo && getNumberOfPages() > 3 &&
                ((isPixelDesign() && !store.getState().isLogo) || store.getState().isDigitalMenuBoard))) {
                window.ConfirmationModal.show({
                    title: 'Sorry, this is not allowed',
                    body: 'Designs with videos can only be downloaded or shared with a maximum of 3 pages.',
                    acceptLabel: 'Ok',
                    hideDecline: true
                });

                return;
            }
        }*/

        const menuId = $this.attr('data-menu');
        const $menu = menuId ? $(menuId) : $this.next();
        let isToggled;
        if ($menu.hasClass('moreOptions')) {
            isToggled = $menu.hasClass('show-more');
        } else {
            isToggled = $menu.hasClass('show');
        }

        if ($this.attr('data-is-nested') !== 'true') {
            if ($this.parent().hasClass('show-more')) {
                $('.dropdown').removeClass('show-more');
                $('.dropdown-menu').removeClass('show-more');
            } else if (!$this.parent().parent().parent().parent().hasClass('moreOptions') || $this.parent().parent().parent().hasClass('moreOptions')) {
                $('.dropdown').removeClass('show-more');
                $('.dropdown-menu').removeClass('show-more');
                $('.dropdown').removeClass('show');
                $('.dropdown-menu').removeClass('show');
            } else {
                $('.dropdown').removeClass('show');
                $('.dropdown-menu').removeClass('show');
            }

            if (store.getState().isMobileVersion) {
                resetMobileContextBar();
            }
        } else if (isToggled) {
            if ($menu.hasClass('moreOptions')) {
                $menu.removeClass('show-more');
                $this.parent().removeClass('show-more');
            } else {
                $menu.removeClass('show');
                $this.parent().removeClass('show');
                $menu.find('.dropdown').removeClass('show');
                $('#picker').removeClass('show');
                $('#text-fill-picker')?.removeClass('show');
                $('#text-line-picker')?.removeClass('show');
            }

            if ($menu.attr('data-close') === 'no') {
                const $next = $menu.next();
                if ($next.length > 0 && $next.hasClass('dropdown-menu')) {
                    $next.removeClass('show');
                    $next.removeClass('show-more');
                }
            }
        }

        if (!isToggled) {
            if ($menu.hasClass('moreOptions')) {
                $menu.addClass('show-more');
                $this.parent().addClass('show-more');
            } else {
                $menu.addClass('show');
                $this.parent().addClass('show');
                if (menuId === '#picker' || menuId === '#text-fill-picker' || menuId === '#text-line-picker') {
                    $this.parent().parent().parent().parent().parent().parent().children().last().addClass('show');
                }
            }

            if (store.getState().isMobileVersion) {
                let dropdownHeight = $menu[0].clientHeight;
                if ($menu.closest('.mobile-bottom-menu').length && $menu.closest('.context-bar').length) {
                    $('.context-bar').css('height', `${dropdownHeight}px`);
                    $('#context-bar .close-context-bar').hide();
                    $menu.css('top', 'calc(100% - ' + dropdownHeight + 'px)');
                }
            }

            if ($('.dropdown-resize').hasClass('show')) {
                $('.search-sizes').val('');
                $('.option-item').each(function () { $(this).show(); })
                $('.size-label').each(function () { $(this).parent().show(); })
                $('.variations').hide();
                $('.search-sizes').trigger('focus');
                $('.no-size-results').hide();
            }

            if ($('.fontfamily-dropdown').hasClass('show')) {
                $('.search-fonts').val('');
                $('.fontfamily-dropdown').find('.scroll').find('li').each(function () { $(this).show() });
                $('.no-matches').hide();
            }

            if ($this.attr('data-reposition') === 'true') {
                const pos = $this.position();
                const buffer = $this.parent().position().top;
                const onRight = $menu.hasClass('on-right');

                if (onRight) {
                    $menu.css('right', 15);
                    $menu.css('left', 'auto');
                } else {
                    $menu.css('left', pos.left + 15);
                    $menu.css('right', 'auto');
                }
                $menu.css('top', pos.top + 15 + buffer);
            }
        }
    }

    if ($('#mob-detect').is(':visible')) {
        $('.dropdown-toggle').on('click', function (ev) {
            init(ev, this);
        });
    } else {
        $document.on('click', '.dropdown-toggle', function (ev) {
            init(ev, this);
        });
    }

    $document.on('click', '.dropdown-menu', function (e) {
        if ($(this).attr('data-close') !== 'no') {
            if (!$(this).hasClass('show-more') || !$(this).parent().hasClass('show-more')) {
                hideDropdown();
            }
        }
        return true;
    });

    $document.on('click', '.dropdown-sticky', (ev) => {
        ev.stopPropagation();
        return true;
    });
};

function pixelToPercent(value, reference) {
    return Math.round(value / reference * 10000000) / 100000;
}

function percentToPixel(value, reference) {
    return Math.round(value * 100000 / 10000000 * reference);
}

class PageDimensions {
    constructor(size) {
        size = deepClone(size);

        this.key = '';
        this.ratio = 0;
        this.width = 0;
        this.height = 0;
        this.fontSize = 0;
        this.zoom = 100;
        this.unit = 'in';

        this.inchWidth = 0;
        this.inchHeight = 0;

        this.pixelWidth = 0;
        this.pixelHeight = 0;

        this.inchToPxRatio = 0;
        this.onePixelRatio = 0;

        this.hasFolds = false;
        this.pageOneFoldOne = 0;
        this.pageOneFoldTwo = 0;
        this.pageOneFoldThree = 0;
        this.pageTwoFoldOne = 0;
        this.pageTwoFoldTwo = 0;
        this.pageTwoFoldThree = 0;

        this.pageOneLabelOne = '';
        this.pageOneLabelTwo = '';
        this.pageOneLabelThree = '';
        this.pageOneLabelFour = '';
        this.pageTwoLabelOne = '';
        this.pageTwoLabelTwo = '';
        this.pageTwoLabelThree = '';
        this.pageTwoLabelFour = '';

        this.size = size;
        this.size.widthInPixels = size.unit === 'in' ? size.width * constants.oneInchInPoints : size.width;
        this.size.heightInPixels = size.unit === 'in' ? size.height * constants.oneInchInPoints : size.height;

        this.isCircular = false;

        this.update(size);
    }

    update(size) {
        this.unit = size.unit;
        this.key = size.key;
        this.ratio = parseFloat(size.ratio);

        this.width = size.unit === 'in' ? size.widthInPixels : size.width;
        this.height = size.unit === 'in' ? size.heightInPixels : size.height;

        this.inchWidth = size.unit === 'in' ? size.width : 0;
        this.inchHeight = size.unit === 'in' ? size.height : 0;

        this.pixelWidth = size.unit === 'px' ? size.width : 0;
        this.pixelHeight = size.unit === 'px' ? size.height : 0;

        this.inchToPxRatio = this.width / this.inchWidth;
        this.onePixelRatio = this.width / this.pixelWidth;

        this.fontSize = size.unit === 'in' ? parseFloat(`${this.inchToPxRatio / constants.oneInchInPoints}`) * constants.defaultFontSizeInPoints : this.height / 50;

        this.isCircular = size.isCircular;

        if (size.folds) {
            this.hasFolds = true;
            if (size.folds[0]) {
                this.pageOneFoldOne = size.folds[0].foldOne;
                this.pageOneFoldTwo = size.folds[0].foldTwo;
                this.pageOneFoldThree = size.folds[0].foldThree;

                this.pageOneLabelOne = size.folds[0].labelOne;
                this.pageOneLabelTwo = size.folds[0].labelTwo;
                this.pageOneLabelThree = size.folds[0].labelThree;
                this.pageOneLabelFour = size.folds[0].labelFour;
            }

            if (size.folds[1]) {
                this.pageTwoFoldOne = size.folds[1].foldOne;
                this.pageTwoFoldTwo = size.folds[1].foldTwo;
                this.pageTwoFoldThree = size.folds[1].foldThree;

                this.pageTwoLabelOne = size.folds[1].labelOne;
                this.pageTwoLabelTwo = size.folds[1].labelTwo;
                this.pageTwoLabelThree = size.folds[1].labelThree;
                this.pageTwoLabelFour = size.folds[1].labelFour;
            }
        }
    }

    updateZoom(zoom) {
        this.zoom = zoom;
        this.width = parseFloat(`${this.size.widthInPixels * (zoom / 100)}`);
        this.height = parseFloat(`${this.size.heightInPixels * (zoom / 100)}`);

        this.inchToPxRatio = this.width / this.inchWidth;
        this.onePixelRatio = this.width / this.pixelWidth;

        this.fontSize = this.unit === 'in' ? parseFloat(`${this.inchToPxRatio / constants.oneInchInPoints}`) * constants.defaultFontSizeInPoints : this.height / 50;
    }
}

let PAGE_DIMENSIONS = null;

const getActiveDimensions = () => PAGE_DIMENSIONS;

function setPageDimensions(size) {
    PAGE_DIMENSIONS = new PageDimensions(size)
}

function getPageDimensions(size) {
    if (!getActiveDimensions()) {
        setPageDimensions(size);
    }
    return getActiveDimensions();
}

function getElementContent($element) {
    if (isText($element)) return $element.find('.txt');
    if (isShape($element) || isIllustration($element)) return $element.find('.svg-content');
    if (isImage($element)) return $element.children('img');
    return $element;
}

function roundToZoomStep(value, floor = false) {
    const { length } = constants.zoomSteps;
    for (let i = 0; i < length; i++) {
        if (value <= constants.zoomSteps[i]) {
            if (!floor) { return constants.zoomSteps[i]; }
            else { return constants.zoomSteps[i - 1]; }
        }
    }
    return value;
}

export function roundTo25(value) {
    let newValue = parseFloat(roundTwoDecimals(parseInt(value * 100) / 100));

    const tempValue = parseInt(newValue * 100);
    const lastOne = tempValue % 10;
    if (lastOne === 9 || lastOne === 4) {
        newValue = roundTwoDecimals((tempValue + 1) / 100);
    } else if (lastOne === 6 || lastOne === 1) {
        newValue = roundTwoDecimals((tempValue - 1) / 100);
    }

    return newValue;
}

export function roundOneDecimalScaled(value) {
    return parseFloat(roundTwoDecimals(parseInt(value * 10) / 10));
}
export function roundTwoDecimalsScaled(value) {
    return parseFloat(roundTwoDecimals(parseInt(value * 100) / 100));
}

const isImage = ($element) => $element && $element?.attr('data-component') === 'image';

const isVideoFrame = ($element) => $element && $element?.attr('data-component') === 'video-frame';

const isQrCode = ($element) => $element && $element?.attr('data-component') === 'qr-code';

const isPage = ($element) => $element && $element?.hasClass('page');

const isFrame = ($element) => $element && $element?.hasClass('frame');

const isFramePreview = ($element) => $element && $element?.attr('data-component') === 'frame-preview';

const isShape = ($element) => $element && $element?.attr('data-component') === 'shape';

const isIllustration = ($element) => $element && $element?.attr('data-component') === 'illustration';

const isCircle = $element => $element && $element?.attr('data-shape') === 'circle';

const isSquare = $element => $element && $element?.attr('data-shape') === 'square';

const isLine = $element => $element && $element?.attr('data-shape') === 'line';

const isText = $element => $element && $element.attr('data-component') === 'text';

const isWrappedText = $element => $element && $element.attr('data-component') === 'text' && $element.attr('data-text-wrapped') === "true";

const isGroup = $element => $element && $element.attr('data-component') === 'group';

const isBackground = $element => $element && $element.attr('data-component') === 'background';

const isResizable = $element => $element && (MenuObject.isMenuObject($element) || isText($element));

const isQRFrameEmpty = $element => $element && ($element.find('.img').attr('src').includes('qr-plc') || $element.find('.img').attr('src').includes('YourQRhere'));

function getPageInViewport($pages) {
    $pages = $pages || $('.page');

    let $page = null;
    let max = 0;

    $pages.each(function () {
        const $this = $(this);
        const percent = store.getState().isMobileVersion ? getInViewportPercentHorizontal($this) : getInViewportPercent($this);
        if (percent > max) {
            max = percent;
            $page = $this;
        }
    });

    return $page;
}

function getPageInViewportNumber() {
    const $page = getPageInViewport($('.page'));
    return $('.page').index($page) + 1;
}

function bringInViewport($element) {
    const scrollTop = $element?.offset().top - 150;
    $(window).scrollTop(scrollTop);
    $('body').animate({ scrollTop }, 500);
}

function bringInViewportMobile($element) {
    const scrollLeft = $element?.offset().left - 5;
    $(window).scrollLeft(scrollLeft);
    $('body').animate({ scrollLeft }, 500);
}

function getInViewportPercent($page) {
    const $window = $(window);

    let elTop = $page.offset().top;
    let elBtm = elTop + $page.get(0).getBoundingClientRect().height;
    const vpTop = $window.scrollTop();
    const vpBtm = vpTop + $window.height();

    if (elTop > vpBtm) return 0;
    if (elBtm < vpTop) return 0;

    if (elTop < vpTop) elTop = vpTop;
    if (elBtm > vpBtm) elBtm = vpBtm;

    const height = elBtm - elTop;
    const vpHeight = vpBtm - vpTop;

    return height / vpHeight * 100;
}

function getInViewportPercentHorizontal($page) {
    const $window = $(window);

    let elLeft = $page.offset().left;
    let elRight = elLeft + $page.get(0).getBoundingClientRect().width;
    const vpLeft = $window.scrollLeft();
    const vpRight = vpLeft + $window.width();

    if (elLeft > elRight) return 0;
    if (elRight < elLeft) return 0;

    if (elLeft < vpLeft) elLeft = vpLeft;
    if (elRight > vpRight) elRight = vpRight;

    const wid = elRight - elLeft;
    const vpWid = vpRight - vpLeft;

    return wid / vpWid * 100;
}

function buildFontsArray(fonts, canvasFonts) {
    canvasFonts.forEach((canvasFont) => {
        const found = getFontByLabel(canvasFont);
        if (found) {
            const { key } = found;
            if (fonts.indexOf(key) === -1) {
                fonts.push(key);
            }
        }
    });

    return fonts;
}

function getPointsInPixels(points, dims) {
    const oneInchInPixels = dims.inchToPxRatio;
    const valueInInches = points / constants.oneInchInPoints;
    return valueInInches * oneInchInPixels;
}

function pixelToInch(pixels) {
    const dims = getActiveDimensions();
    return Math.ceil(pixels / dims.inchToPxRatio * 100) / 100;
}

function inchToPixel(inches) {
    const dims = getActiveDimensions();
    return Math.round(inches * dims.inchToPxRatio * 100000) / 100000;
}

function inchToPercent(inches, reference) {
    return pixelToPercent(inchToPixel(inches), reference)
}

function getFontsToRemove(elementFonts, designFonts) {
    const toRemove = [];

    elementFonts.forEach((font) => {
        if (designFonts.indexOf(font) === -1) {
            toRemove.push(font);
        }
    });

    return toRemove;
}

const toDegrees = angle => angle * (180 / Math.PI);

const toRadians = angle => angle * (Math.PI / 180);

const getNumberOfPages = () => $('.page').length;

const isA4 = (size) => size && size.indexOf('a4') > -1;

const isFlyer = (size) => size && flyerSizes.indexOf(size) > -1;

const isSocialDesign = (size) => size === 'fb-post' || size === 'fb-story' || size === 'insta-post' || size === 'insta-story';

const isBusinessCard = (size) => size && size.indexOf('biz-card') > -1;

const isLoyaltyCard = (size) => size && size.indexOf('loyalty-card') > -1;

const isCommentCard = (size) => size && size.indexOf('comment-card') > -1;

const isRackCard = (size) => size && size.indexOf('rack-card') > -1;

const isGiftCertificate = (size) => size && size.indexOf('gift-certificate') > -1;

const isPortraitCard = (size) => size === 'biz-card-portrait' || size === 'loyalty-card-portrait' || size === 'comment-card-portrait' || size === 'rack-card-portrait' || size === 'sticker-portrait' | size === 'label-portrait';

const isMenu = (size) => size && size.indexOf('menu') > -1 && size.indexOf('kids-menu') === -1 && size.indexOf('folded-menu') === -1 && size.indexOf('half-page-menu') === -1 && size.indexOf('menu-mailer') === -1 && size.indexOf('digital-menu-board') === -1;

const isKidsMenu = (size) => size && size.indexOf('kids-menu') > -1;

const isTakeout = (size) => size && size.indexOf('takeout') > -1;

const isFoldedMenu = (size) => size && size.indexOf('folded-menu') > -1;

const isHalfPageMenu = (size) => size && size.indexOf('half-page-menu') > -1;

const isFacebookPost = (size) => size && size.indexOf('fb-post') > -1;

const isFacebookStory = (size) => size && size.indexOf('fb-story') > -1;

const isInstagramPost = (size) => size && size.indexOf('insta-post') > -1;

const isInstagramStory = (size) => size && size.indexOf('insta-story') > -1;

const isTableTent = (size) => size && size.indexOf('table-tent') > -1 && size.indexOf('table-tent-insert') === -1;

const isTableTentInsert = (size) => size && size.indexOf('table-tent-insert') > -1;

const isPoster = (size) => size && size.indexOf('poster') > -1;

const isDigitalMenuBoard = (size) => size && size.indexOf('digital-menu-board') > -1;

const isMenuMailer = (size) => size && size.indexOf('menu-mailer') > -1;

const isSticker = (size) => size && size.indexOf('sticker') > -1;

const isLabel = (size) => size && size.indexOf('label') > -1;

const isSandwichBoard = (size) => size && size.indexOf('sandwich-board') > -1;

const isCustomSize = (size) => size && size.indexOf('custom') > -1;

const isLogo = (size) => size && size.indexOf('logo') > -1;

const getDesignUnit = size => isSocialDesign(size) || isLogo(size) ? 'px' : 'in';

function hex2rgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}


const hexDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'a', 'b', 'c', 'd', 'e', 'f'];

function rgb2hex(rgb) {
    if (rgb.indexOf('rgba') > -1) return rgbaToHex(rgb);

    rgb = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    return rgb ? `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}` : constants.DEFAULT_TEXT_COLOR;
}

const hex = (x) => (isNaN(x) ? '00' : hexDigits[(x - x % 16) / 16] + hexDigits[x % 16]);

const trim = (str) => str.replace(/^\s+|\s+$/gm, '');

function rgbaToHex(rgba) {
    const parts = rgba.substring(rgba.indexOf('(')).split(',');
    const r = parseInt(trim(parts[0].substring(1)), 10);
    const g = parseInt(trim(parts[1]), 10);
    const b = parseInt(trim(parts[2]), 10);
    const a = parseFloat(trim(parts[3].substring(0, parts[3].length - 1))).toFixed(2);

    return (`#${r.toString(16)}${g.toString(16)}${b.toString(16)}${(a * 255).toString(16).substring(0, 2)}`);
}

function getHeight($element, unit) {
    let height = 0;

    if (unit === 'in') {
        height = $element?.attr('data-inch-height');
        if (!height || height === '0') height = pixelToInch(data.shapeHeight || $element.height());
    } else {
        height = $element.attr('data-px-height');
        if (!height || height === '0') height = Math.ceil($element.height() * (1 / getActiveDimensions().onePixelRatio) * 100) / 100;
    }

    return height;
}

function getWidth($element, unit) {
    let width = 0;

    if (unit === 'in') {
        width = $element.attr('data-inch-width');
        if (!width || width === '0') width = pixelToInch($element.width());
    } else {
        width = $element?.attr('data-px-width');
        if (!width || width === '0' || width === $element?.attr('data-inch-width')) {
            width = Math.ceil($element?.width() * (1 / getActiveDimensions().onePixelRatio) * 100) / 100;
        }
    }

    return width;
}

function isSideResize(event) {
    const { edges } = event;

    let count = 0;
    if (edges.left) count += 1;
    if (edges.top) count += 1;
    if (edges.right) count += 1;
    if (edges.bottom) count += 1;

    return count === 1;
}

const isInGroup = ($it) => $it && (!!$it.attr('data-in-group') || getGroup($it).length > 0);

const getGroup = ($it) => $it.parents('[data-component="group"]');

function getSizeType(size) {
    switch (size) {
        case 'letter':
        case 'letter-landscape':
        case 'tabloid':
        case 'tabloid-landscape':
        case 'a4':
        case 'a4-landscape':
            return 'Flyer';

        case 'biz-card-landscape':
        case 'biz-card-portrait':
        case 'biz-card-square':
        case 'biz-card-circular':
            return 'Business Card';

        case 'fb-post':
            return 'Facebook Post';

        case 'fb-story':
            return 'Facebook Story';

        case 'insta-post':
            return 'Instagram Post';

        case 'insta-story':
            return 'Instagram Story';

        case 'menu-letter':
        case 'menu-tabloid':
        case 'menu-a4':
        case 'menu-legal':
            return 'Menu';

        case 'loyalty-card-landscape':
        case 'loyalty-card-portrait':
            return 'Loyalty Card';

        case 'kids-menu-letter-portrait':
        case 'kids-menu-letter-landscape':
        case 'kids-menu-tabloid-portrait':
        case 'kids-menu-tabloid-landscape':
        case 'kids-menu-legal-portrait':
        case 'kids-menu-legal-landscape':
            return 'Kids Menu'

        case 'comment-card-portrait':
            return 'Comment Card';

        case 'rack-card-portrait':
            return 'Rack Card';

        case 'gift-certificate-landscape':
            return 'Gift Certificate';

        case 'takeout-letter-trifold':
        case 'takeout-letter-bifold':
        case 'takeout-legal-trifold':
        case 'takeout-legal-quadfold':
        case 'takeout-tabloid-trifold':
            return 'Takeout';

        case 'folded-menu-letter-trifold':
        case 'folded-menu-letter-bifold':
        case 'folded-menu-legal-trifold':
        case 'folded-menu-tabloid-trifold':
            return 'Folded Menu';

        case 'half-page-menu':
        case 'half-page-menu-long':
        case 'half-page-menu-wide':
        case 'half-page-menu-wide-landscape':
            return 'Half Page';

        case 'table-tent':
        case 'table-tent-insert':
        case 'table-tent-insert-4x6':
            return 'Table Tent';

        case 'poster':
            return 'Poster';

        case 'digital-menu-board':
            return 'Digital Menu Board';

        case 'menu-mailer-tabloid-trifold':
            return 'Menu Mailer';

        case 'sticker-landscape':
        case 'sticker-portrait':
        case 'sticker-square':
            return 'Sticker';

        case 'label-landscape':
        case 'label-portrait':
        case 'label-square':
            return 'Label';

        case 'sandwich-board':
            return 'Sandwich Board';

        case 'logo':
            return 'Logo';
    }
}

function getLongtailType(size) {
    switch (size) {
        case 'letter':
        case 'letter-landscape':
        case 'tabloid':
        case 'tabloid-landscape':
        case 'a4':
        case 'a4-landscape':
            return 'flyer';

        case 'biz-card-landscape':
        case 'biz-card-portrait':
        case 'biz-card-square':
        case 'biz-card-circular':
            return 'business-card';

        case 'fb-post':
        case 'insta-post':
            return 'social-post';

        case 'fb-story':
        case 'insta-story':
            return 'story';

        case 'menu-letter':
        case 'menu-tabloid':
        case 'menu-a4':
        case 'menu-legal':
            return 'menu';

        case 'loyalty-card-landscape':
        case 'loyalty-card-portrait':
            return 'loyalty-card';

        case 'kids-menu-letter-portrait':
        case 'kids-menu-letter-landscape':
        case 'kids-menu-tabloid-portrait':
        case 'kids-menu-tabloid-landscape':
        case 'kids-menu-legal-portrait':
        case 'kids-menu-legal-landscape':
            return 'kids-menu';

        case 'comment-card-portrait':
            return 'comment-card';

        case 'rack-card-portrait':
            return 'rack-card';

        case 'gift-certificate-landscape':
            return 'gift-certificate';

        case 'takeout-letter-trifold':
        case 'takeout-letter-bifold':
        case 'takeout-legal-trifold':
        case 'takeout-legal-quadfold':
        case 'takeout-tabloid-trifold':
            return 'takeout-menu';

        case 'folded-menu-letter-trifold':
        case 'folded-menu-letter-bifold':
        case 'folded-menu-legal-trifold':
        case 'folded-menu-tabloid-trifold':
            return 'folded-menu';

        case 'half-page-menu':
        case 'half-page-menu-long':
        case 'half-page-menu-wide':
        case 'half-page-menu-wide-landscape':
            return 'half-page-menu';

        case 'table-tent':
            return 'table-tent';
        case 'table-tent-insert':
        case 'table-tent-insert-4x6':
            return 'table-insert';

        case 'poster':
            return 'poster';

        case 'digital-menu-board':
        case 'digital-menu-board-portrait':
            return 'digital-menu-board';

        case 'menu-mailer-tabloid-trifold':
            return 'menu-mailer';

        case 'sticker-landscape':
        case 'sticker-portrait':
        case 'sticker-square':
            return 'sticker';

        case 'label-landscape':
        case 'label-portrait':
        case 'label-square':
            return 'label';

        case 'sandwich-board':
            return 'sandwich-board';

        case 'logo':
            return 'logo';
    }
}

const getStatusLabel = (status) => constants.statuses.find((s) => s.id === status).label;

function isOffPage($target) {
    const $page = $target.parents('.page');

    const pos = {
        top: $target.position().top,
        left: $target.position().left,
        bottom: $target.position().top + $target.height(),
        right: $target.position().left + $target.width()
    };

    if (constants.SCALED_ZOOM_APPROACH) {
        const zoom = store.getState().zoom / 100;
        pos.top = pos.top / zoom;
        pos.left = pos.left / zoom;
        pos.bottom = pos.bottom / zoom;
        pos.right = pos.right / zoom;
    }

    const page = {
        width: $page.width(),
        height: $page.height()
    };

    if (pos.right < 0 || pos.bottom < 0) return true;
    return pos.left > page.width || pos.top > page.height;
}

function getPositionPage(element) {
    const $pages = $('.page');
    const zoomfactor = store.getState().zoom / 100;

    const $element = $(element);
    const $ogPage = $element?.parents('.page');
    let elStart = null;
    let elEnd = null;
    let elDist = null;

    if (store.getState().isMobileVersion) {
        elStart = $element?.offset().left;
        elDist = $element[0].getBoundingClientRect().width * zoomfactor;
        elEnd = elStart + elDist;
    } else {
        elStart = $element?.offset().top;
        elDist = $element[0].getBoundingClientRect().height * zoomfactor;
        elEnd = elStart + elDist;
    }

    let $startPage = null;
    let $endPage = null;

    $pages.each(function () {
        const $this = $(this);
        if ($this.css('display') !== 'none') {
            let pageStart = null;
            let pageEnd = null;
            let pageDist = null;

            if (store.getState().isMobileVersion) {
                pageStart = $this.offset().left;
                pageDist = $this.width() * zoomfactor;
                pageEnd = pageStart + pageDist;
            } else {
                pageStart = $this.offset().top;
                pageDist = $this.height() * zoomfactor;
                pageEnd = pageStart + pageDist;
            }

            if (elStart >= pageStart && elStart <= pageEnd) {
                $startPage = $this;
            }

            if (elEnd >= pageStart && elEnd <= pageEnd) {
                $endPage = $this;
            }

            if ($element?.height() * zoomfactor > pageDist) {
                if (elStart <= pageStart && elEnd >= pageEnd) {
                    $startPage = $this;
                    $endPage = $this;
                }
            }
        }
    });

    let $returnPage = $ogPage;
    if (!$startPage && !$endPage) {
        $returnPage = null;
    } else if (!$startPage && $endPage) {
        $returnPage = $endPage;
    } else if ($startPage && !$endPage) {
        $returnPage = $startPage;
    } else if ($startPage && $endPage) {
        if ($startPage.attr('id') === $endPage.attr('id')) {
            $returnPage = $startPage;
        } else if ($startPage.attr('id') !== $ogPage.attr('id') && $endPage.attr('id') !== $ogPage.attr('id')) {
            $returnPage = $startPage;
        }
    }
    return $returnPage;
}

const hasDesignName = (name) => !!name && name !== 'Untitled Design';

function getFriendlySize(size) {
    if (size.indexOf('-') < 0) return size;

    const bits = size.split('-');
    const len = bits.length;
    return `${bits[len - 2]} ${bits[len - 1]}`
}

function getCssRule($element, key) {
    const style = $element?.attr('style');
    if (!style || style.indexOf(key) === -1) return null;

    const bits = style.split(`${key}:`);
    const aux = bits[1];
    if (aux.indexOf(';') === -1) return aux.trim();

    return aux.split(';')[0].trim()
}

const isSelected = ($element) => $element?.hasClass('in-focus');

function stripDoubleNewLine(txt) {
    txt = txt.replace(/\n\n/g, '\n');
    txt = txt.replace(/\r\n\r\n/g, '\n');
    return txt;
}

const nextTick = (callback, time = 0) => window.setTimeout(callback, time);

const timeout = (ms) => new Promise(resolve => setTimeout(resolve, ms));

function onFontsLoaded(callback) {
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
            if (callback) callback();
        });
    } else {
        if (isSafari() || isEdge()) {
            setTimeout(() => {
                callback();
            }, 250);
        } else {
            callback();
        }
    }
}

const isSafari = () => !!window.safari;

const isFirefox = () => window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const isEdge = () => window.navigator.userAgent.toLowerCase().indexOf('edge') > -1;

const isWindows = () => window.navigator.userAgent.toLowerCase().indexOf('win') > -1;

const isGroupAlteringStyle = (key) => constants.GROUP_ALTERING_STYLES.indexOf(key) > -1;

const isLineHeightAlteringStyle = (key) => constants.LINE_HEIGHT_ALTERING_STYLES.indexOf(key) > -1;

function getFontSize($element) {
    let size = parseInt(`${$element[0].style.fontSize / 10}`, 10);
    if (getActiveDimensions().unit === 'px') size /= 1.3;
    return size;
}

const isContentEditable = ($element) => ($element ? $element.find('.ql-editor').attr('contenteditable') : 'true') === 'true';

const setContentEditable = ($element, editable) => {
    if ($element) {
        $element.find('.ql-editor').attr('contenteditable', editable);
    }
}

function convertInlineSize(elementPercent) {
    let size = parseFloat(elementPercent) / 10;
    if (getActiveDimensions().unit === 'px') {
        size = Math.round(size / 1.3);
    }
    return size;
}

function getNextWordBreak(str, withSpace = true) {
    const line = str.indexOf('\n') || 0;
    const space = withSpace ? str.indexOf(' ') : line;

    if (space === -1) {
        return line === -1 ? str.length : line;
    }
    return space < line ? space : line;
}

function getPrevWordBreak(str, withSpace = true) {
    const line = str.lastIndexOf('\n') || 0;
    const space = withSpace ? str.lastIndexOf(' ') : line;
    return str.length - (space > line ? space : line) - 1;
}

function getComponentLineHeight($component) {
    if (!$component.attr('inl')) {
        const $txt = $component.find('.txt');
        let lineHeight = getCssRule($txt, 'line-height');
        if (!lineHeight) {
            lineHeight = getCssRule($component, 'line-height');
        }
        return lineHeight ? parseFloat(lineHeight) : 1;
    }

    const $p = $component.find('.txt').find('p').eq(0);
    if ($p.length === 0) return 1;
    const lineHeight = getCssRule($p, 'line-height');
    return lineHeight ? parseFloat(lineHeight) : 1;
}

const cleanFont = (family) => (family || '').replace(/["']/g, '');

const toCamelCase = (str) => str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

function asParagraph($bit) {
    if ($bit.is('p')) return $bit;

    const style = $bit.attr('style');
    const $p = $(`<p>${$bit.html()}</p>`);
    $p.attr('style', style);

    return $p;
}

const formatLetterSpacing = (value) => (parseFloat(value || 0) * 10).toFixed(1);

function getStrokeWidth(strokeWidth, width) {
    const strokeWidthPercent = pixelToPercent(strokeWidth, width);
    return strokeWidthPercent * 4;
}

const isNewLineChar = (char) => char === '\n' || char === '\n\r';

const isControlKey = (code) => [constants.CTRL_KEY, constants.ALT_KEY, constants.META_KEY, constants.CMD_KEY, constants.CAPS_KEY, constants.SHIFT_KEY].indexOf(code);

function isTestUrl() {
    if (!location || !location.pathname) return false;
    return location.origin.indexOf('localhost') > -1
        || location.pathname.indexOf('mkdesigner') > -1
        || location.pathname.indexOf('timber-test') > -1
        || location.pathname.indexOf('timber-dev1') > -1
        || location.pathname.indexOf('timber-dev2') > -1;
}

function isLocalhost() {
    if (!location || !location.pathname) return false;
    return location.origin.indexOf('localhost') > -1;
}

export function showDuplicatedDesign(designName) {
    window.ConfirmationModal.show({
        title: 'Duplicated design name',
        body: `You already have a design called <b>${designName}</b>.<br/> Please choose another name.`,
        acceptLabel: 'OK',
        hideDecline: true,
        onAccept: () => {
            window.ConfirmationModal.hide();
        },
    })
}

function showAutoSaveFailed() {
    if (!window.ConfirmationModal.isVisible()) {
        window.ConfirmationModal.show({
            title: 'Oops!',
            body: 'AutoSave had a misfire.',
            acceptLabel: 'Save Design Now',
            size: 'medium',
            hideDecline: true,
            onAccept: () => {
                designService.updateDesignHtml({
                    id: store.getState().designId,
                    lastSavedTag: store.getState().lastSavedTag,
                    hasChanged: store.getState().hasChanged,
                    html: Renderer.getPagesContent(),
                    hasVideo: store.getState().hasVideo
                }).then(() => {
                    window.FAILED_SAVES = 0;
                }).catch(() => {
                    showAutoSaveReallyFailed();
                });
            }
        });
    }
}

function showAutoSaveReallyFailed() {
    if (!window.ConfirmationModal.isVisible()) {
        window.ConfirmationModal.show({
            title: 'We\'re sorry, save is having issues',
            body: 'Don\'t lose your changes. Contact the service team.',
            acceptLabel: 'Refresh Page',
            size: 'medium',
            hideDecline: true,
            onAccept: () => {
                window.location.reload();
            }
        });
    }
}

export function showSaveHangingModal() {
    if (!window.ConfirmationModal.isVisible()) {
        window.ConfirmationModal.show({
            title: "We're sorry! We're having a problem with Saving.",
            body: "Please exit and reopen the design to clear the problem.",
            acceptLabel: 'Exit',
            size: 'medium',
            hideDecline: true,
            onAccept: () => {
                window.ignoreBeforeUnloadWarning = true;
                window.location.href = "/";
            }
        });
    }
}

export function showDesignInUseModal() {
    if (!window.ConfirmationModal.isVisible()) {
        window.ConfirmationModal.show({
            title: 'We couldn\'t save your design.',
            body: 'You\'re not on the latest version of this design.<br/>Please reload the page to get the latest version.',
            acceptLabel: 'Reload page',
            size: 'medium',
            hideDecline: true,
            onAccept: () => {
                window.ignoreBeforeUnloadWarning = true;
                window.location.reload();
            }
        });
    }
}

export function showChangedByPos() {
    if (!window.ConfirmationModal.isVisible()) {
        window.posConfirmationModalVisible = true;
        window.ConfirmationModal.show({
            title: "POS Updates",
            body: "POS updates are now available for your Menu",
            acceptLabel: "Get Updates",
            size: 'medium',
            hideDecline: true,
            onAccept: () => {
                window.ignoreBeforeUnloadWarning = true;
                window.forcedExit = true;
                window.location.reload();
            }
        });
    }
}

function showConfirmRevisionRestoreModal(since, callback) {
    window.ConfirmationModal.show({
        title: 'Restore this version?',
        body: `Your current document will revert to the version from ${since}`,
        acceptLabel: 'Restore',
        declineLabel: 'Cancel',
        onAccept: () => {
            callback()
        },
        onDecline: () => {
            window.ConfirmationModal.hide();
        }
    });
}

function showSessionInactiveModal() {
    if (!window.ConfirmationModal.isVisible()) {
        let seconds = 0;
        const sessionIdleCountdownInterval = setInterval(() => {
            seconds += 1;
            if (seconds <= 60) {
                $('#session_leave_countdown').text(60 - seconds);
            } else {
                clearInterval(sessionIdleCountdownInterval);
                saveDesign(() => {
                    window.ignoreBeforeUnloadWarning = true;
                    window.location = MyMenusPath;
                })
            }
        }, 1000);

        window.ConfirmationModal.show({
            title: 'Are you still there?',
            body: `Your session will expire in <span id="session_leave_countdown">60</span> seconds. Your changes are saved.`,
            acceptLabel: 'Continue editing',
            declineLabel: 'Exit',
            size: 'medium',
            onAccept: () => {
                clearInterval(sessionIdleCountdownInterval);
                resetSessionIdle();
                saveDesign();
                window.ConfirmationModal.hide();
            },
            onDecline: () => {
                clearInterval(sessionIdleCountdownInterval);
                saveDesign(() => {
                    window.ignoreBeforeUnloadWarning = true;
                    window.location = MyMenusPath;
                })
            }
        });
    }
}

const exitWithoutPermissionTimer = 20;

export function showInUseOnOpenModal() {
    if (!window.ConfirmationModal.isVisible()) {
        let seconds = 0;
        const sessionExitCountdownInterval = setInterval(() => {
            seconds += 1;
            if (seconds <= exitWithoutPermissionTimer) {
                $('#session_leave_countdown').text(exitWithoutPermissionTimer - seconds);
            } else {
                clearInterval(sessionExitCountdownInterval);
                window.ignoreBeforeUnloadWarning = true;
                window.forcedExit = true;
                window.location = MyMenusPath;
            }
        }, 1000);

        window.ConfirmationModal.show({
            title: 'This design is currently in use.',
            body: `<span class="text-dark">Exiting in <span id="session_leave_countdown">${exitWithoutPermissionTimer}</span> seconds.</span>`,
            acceptLabel: 'Exit now',
            hideDecline: true,
            bgOpaque: true,
            hideOnAccept: false,
            size: 'medium',
            onAccept: () => {
                clearInterval(sessionExitCountdownInterval);
                window.ignoreBeforeUnloadWarning = true;
                window.forcedExit = true;
                window.location = MyMenusPath;
            }
        });
    }
}

export function showNoLocationPermissionModal() {
    if (!window.ConfirmationModal.isVisible()) {
        let seconds = 0;
        const sessionExitCountdownInterval = setInterval(() => {
            seconds += 1;
            if (seconds <= exitWithoutPermissionTimer) {
                $('#session_leave_countdown').text(exitWithoutPermissionTimer - seconds);
            } else {
                clearInterval(sessionExitCountdownInterval);
                window.ignoreBeforeUnloadWarning = true;
                window.forcedExit = true;
                window.location = MyMenusPath;
            }
        }, 1000);

        window.ConfirmationModal.show({
            title: 'You do not have permission to access this location. Ask account owner to allow and try again.',
            body: `<span class="text-dark">Exiting in <span id="session_leave_countdown">${exitWithoutPermissionTimer}</span> seconds.</span>`,
            acceptLabel: 'Exit now',
            hideDecline: true,
            bgOpaque: true,
            hideOnAccept: false,
            size: 'medium',
            onAccept: () => {
                clearInterval(sessionExitCountdownInterval);
                window.ignoreBeforeUnloadWarning = true;
                window.forcedExit = true;
                window.location = MyMenusPath;
            }
        });
    }
}

export function showDotLeaderWarningModal() {
    window.ConfirmationModal.show({
        title: 'Warning',
        body: 'Dot leaders have problems in the Safari browser. We recommend you switch to Chrome.',
        acceptLabel: 'Got it, thanks!',
        size: 'medium',
        hideDecline: true,
        onAccept: () => {
            window.ConfirmationModal.hide();
        }
    });
}

export function detectDotLeaderLayout() {
    return $('.pages').find('.menu-object').toArray().some((el) => $(el).attr('data-layout').includes('-with-dot-leaders'));
}

function ellipsis(value, length) {
    if (value?.length <= length) return value;
    return `${value?.slice(0, length)}...`;
}

function isLocked($element) {
    return $element?.hasClass('locked');
}

function isHyperlink($element) {
    return $element?.hasClass('hyperlink');
}

function getHref($element) {
    return $element?.attr('data-href');
}

const debounce = function (func, wait = 350, immediate = false) {
    let timeout;
    return function () {
        const context = this; const args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            timeout = null;
            if (!immediate) func.apply(context, args);
        }, wait);
        if (immediate && !timeout) func.apply(context, args);
    };
};

let idleTime = 0;
function initIdleCheck() {
    setInterval(() => {
        idleTime += 1;
        if (idleTime > 10) {
            checkForOpenTabs();
        }
    }, 60000);
}

function resetIdle() {
    idleTime = 0;
}

let sessionIdleTime = 0;
const sessionInactiveInterval = 30;
function initSessionIdleCheck() {
    if (data.member.isAdmin) return;
    if (!store.getState().isLogged) return;
    if (store.getState().isTemplate) return;

    setInterval(() => {
        sessionIdleTime += 1;
        if (sessionIdleTime === sessionInactiveInterval) {
            showSessionInactiveModal();
        } else if (sessionIdleTime - 1 >= sessionInactiveInterval) {
            saveDesign(() => {
                window.ignoreBeforeUnloadWarning = true;
                window.location = MyMenusPath;
            })
        }
    }, 60 * 1000);
}

function resetSessionIdle() {
    if (!store.getState().isLogged) return;
    sessionIdleTime = 0
}

let activeTime = 0;
function initRegCheck() {
    if (store.getState().isLogged) return;

    let activeInterval = setInterval(() => {
        activeTime += 1;
        if (activeTime > 4) {
            if (!store.getState().isLogged) {
                toLogIn();
            }
            clearInterval(activeInterval)
        }
    }, 60000);
}

function checkForOpenTabs() {
    if (!window.ConfirmationModal.isVisible()) {
        designService.checkLastSaveTag(store.getState().designId, store.getState().lastSavedTag).then((response) => {
            if (response.data === false) {
                showDesignInUseModal();
            }
        })
    }
}

function initCustomColorsLoad(memberId) {
    if (memberId && memberId != -1) {
        const promise = colorService.getMemberColors(memberId);
        promise.then(response => {
            if (response.data && response.data.length > 0) {
                constants.customColors = [...response.data];
            }
        });
    }
}

function loadUploadedFont(uploadedFont) {
    const url = `url(${(isLocalhost() ? constants.S3_PATH : constants.SECURE_S3_PATH) + uploadedFont.path.replace(/ /g, '%20')})`;
    const className = "node_family_" + uploadedFont.name.toLowerCase().replace(/ /g, '-');

    const font = new FontFace(uploadedFont.name, url);
    font.load();
    document.fonts.add(font);

    let element = document.getElementById('uploaded-fonts-style');

    if (!element) {
        element = document.createElement("style");
        element.id = 'uploaded-fonts-style';

        element.innerHTML = `.${className} { font-family: '${uploadedFont.name}';}`

        const header = document.getElementsByTagName("HEAD")[0];
        header.appendChild(element);
    } else {
        element.innerHTML += `\n.${className} { font-family: '${uploadedFont.name}';}`
    }
}

function initCustomFontsLoad(brand) {
    if (brand.proFonts) {
        let customFontNames = [];

        brand.proFonts.forEach(proFont => {
            proFont.key = "node_family_" + proFont.name.toLowerCase().replace(/ /g, '-');
            proFont.label = proFont.name;
            proFont.font = proFont.name;
            customFontNames.push(proFont.name);
        })

        constants.customFonts = [...brand.proFonts];

        if (customFontNames.length > 0) {
            WebFont.load({
                google: {
                    families: customFontNames
                }
            });
        }

        const uploadedFonts = [...brand.uploadedFonts.sort((a, b) => b.createdDate - a.createdDate)];

        uploadedFonts.forEach(uploadedFont => {
            uploadedFont.name = uploadedFont.name.replace('.otf', '').replace('.ttf', '').replace('.woff', '');
            uploadedFont.key = "node_family_" + uploadedFont.name.toLowerCase().replace(/ /g, '-');
            uploadedFont.label = uploadedFont.name;
            uploadedFont.font = uploadedFont.name;

            //load font
            loadUploadedFont(uploadedFont);
        })

        constants.customFonts = [...constants.customFonts, ...uploadedFonts];


    }
}

function initFavoriteFontsLoad(brand) {
    if (brand.fontHeader && brand.fontHeader.name) {
        constants.favoriteFonts['HEADER'] = brand.fontHeader;
        constants.favoriteFonts['HEADER'].key = "node_family_" + brand.fontHeader.name.toLowerCase().replace(/ /g, '-');
        constants.favoriteFonts['HEADER'].label = brand.fontHeader.name;
    }

    if (brand.fontSubheader && brand.fontSubheader.name) {
        constants.favoriteFonts['SUB-HEADER'] = brand.fontSubheader;
        constants.favoriteFonts['SUB-HEADER'].key = "node_family_" + brand.fontSubheader.name.toLowerCase().replace(/ /g, '-');
        constants.favoriteFonts['SUB-HEADER'].label = brand.fontSubheader.name;
    }

    if (brand.fontBodyText && brand.fontBodyText.name) {
        constants.favoriteFonts['BODY TEXT'] = brand.fontBodyText;
        constants.favoriteFonts['BODY TEXT'].key = "node_family_" + brand.fontBodyText.name.toLowerCase().replace(/ /g, '-');
        constants.favoriteFonts['BODY TEXT'].label = brand.fontBodyText.name;
    }
}

const isAuthOverlay = () => store.getState().isAuthOverlay;

const getPageNumber = ($element) => parseInt($element?.closest('.page').attr('id')?.replace('page-', '') || '0');

const getPageId = ($element) => $element?.parents('.page').attr('id');

function getPanelFold($element) {
    let panel = 0;
    const dims = getActiveDimensions();
    let left = parseInt($element?.position().left);
    if (constants.SCALED_ZOOM_APPROACH) {
        const zoom = store.getState().zoom / 100;
        left = left / zoom;
    }
    switch (getPageNumber($element)) {
        case 1:
            if (left < parseInt(inchToPixel(dims.pageOneFoldOne)))
                panel = 1
            else if ((dims.pageOneFoldTwo && left >= parseInt(inchToPixel(dims.pageOneFoldOne)) && left < parseInt(inchToPixel(dims.pageOneFoldTwo))) ||
                (!dims.pageOneFoldTwo && left >= parseInt(inchToPixel(dims.pageOneFoldOne))))
                panel = 2
            else if ((dims.pageOneFoldThree && left >= parseInt(inchToPixel(dims.pageOneFoldTwo)) && left < parseInt(inchToPixel(dims.pageOneFoldThree))) ||
                (!dims.pageOneFoldThree && left >= parseInt(inchToPixel(dims.pageOneFoldTwo))))
                panel = 3
            else if (left >= parseInt(inchToPixel(dims.pageOneFoldThree)))
                panel = 4
            break
        case 2:
            if (left < parseInt(inchToPixel(dims.pageTwoFoldOne)))
                panel = 1
            else if ((dims.pageTwoFoldTwo && left >= parseInt(inchToPixel(dims.pageTwoFoldOne)) && left < parseInt(inchToPixel(dims.pageTwoFoldTwo))) ||
                (!dims.pageTwoFoldTwo && left >= parseInt(inchToPixel(dims.pageTwoFoldOne))))
                panel = 2
            else if ((dims.pageTwoFoldThree && left >= parseInt(inchToPixel(dims.pageTwoFoldTwo)) && left < parseInt(inchToPixel(dims.pageTwoFoldThree))) ||
                (!dims.pageTwoFoldThree && left >= parseInt(inchToPixel(dims.pageTwoFoldTwo))))
                panel = 3
            else if (left >= parseInt(inchToPixel(dims.pageTwoFoldThree)))
                panel = 4
            break
    }

    return panel
}

function objectsAreEqual(first, second) {
    let equal = true;
    Object.keys(first).forEach(key => {
        if (first[key] !== second[key]) {
            equal = false;
        }
    });
    return equal;
}

export function saveDesign(callback) {
    if (store.getState().isVersionPreview) return;

    store.dispatch(actions.setIsSaved(false));
    store.dispatch(actions.setIsSaving(true));

    designService.updateDesignHtml({
        id: store.getState().designId,
        html: Renderer.getPagesContent(),
        lastSavedTag: store.getState().lastSavedTag,
        hasVideo: store.getState().hasVideo
    }).then(() => {
        store.dispatch(actions.setIsSaved(true));
        store.dispatch(actions.setIsSaving(false));
        if (!!callback) {
            callback();
        }
    })
}

const supportsFullscreen = () => document.fullscreenEnabled || document.webkitFullscreenEnabled;

const fullScreenPreview = (options) => {
    if (constants.SCALED_FULL_SCREEN_APPROACH) {
        window.scrollTo(0, 0);
        if (constants.SCALED_ZOOM_APPROACH) {
            $('#canvas').css({ 'transform': '', 'padding-top': '', 'height': '', 'width': '', 'overflow-y': 'inherit' });
            $('#pages').css({ 'transform': '', 'height': '', 'margin-top': '' });
            $('#preview-block').css({ 'height': '' });
        } else {
            Renderer.setZoom(100);
        }
    }

    document.querySelector('body').classList.add('full-screen');
    if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
    } else {
        document.documentElement.webkitRequestFullScreen();
    }
    Renderer.deselectAll();
    $('.g-extra').hide();

    if (constants.SCALED_FULL_SCREEN_APPROACH) {

        let pAdvisorWidth = 0;
        if (typeof (options) !== 'undefined' && !options.fromPA) {
            pAdvisorWidth = $('#pa-advisor').get(0).getBoundingClientRect().width;
            if (pAdvisorWidth > 0) {
                pAdvisorWidth += 60;
            }
        }

        nextTick(() => {
            //When the user changes zoom, because of the calculations that occur in order to resize everything on the page, there might be some small differences between zoom levels.
            //Because of this, we should not re-calculate anything when going to the print advisor so that the user can see the zoom level that he left the menu on.
            //Scale with transform in order to maintain the zoom level that the user used for the formatting.

            const $pages = $('#pages');
            //Get the size of the first page
            const pageDimensions = $('#page-1').get(0).getBoundingClientRect();
            let height = pageDimensions.height;

            //window.innerHeight window height , screen.height
            //Calculate the zoom factor based on the screen height. In full screen the whole screen height will be used.
            let fsResizeFactor = 100;
            const heightCorrectionFactor = 100;

            const pageSize = getActiveDimensions();
            if (pageSize.key === 'gift-certificate-landscape') {
                height = height * 1.25;
            }

            fsResizeFactor = ((screen.height - heightCorrectionFactor) * 100 / height) * 100 / 100;


            $('#pages').css('transform', 'scale(' + fsResizeFactor / 100 + ')');
            $('#pages').css({
                'transform-origin': '50% top 0px',
                '-webkit-transform-origin': '50% top 0px'
            });
            const pagesDimensions = $pages.get(0).getBoundingClientRect();
            $('#canvas').css({ 'height': (pagesDimensions.height + 100) + 'px' });
            //nightmare
            if (isFirefox() && (fsResizeFactor / 100) < 1) {
                //$('#canvas').css({'height' : ((pagesDimensions.height / (fsResizeFactor / 100)) + 100) + 'px'});
                $('#canvas').css({ 'overflow-y': 'hidden' });
            }
        })
    } else {
        nextTick(() => Renderer.fitToPage(true, options));
    }
}

function disableFullScreenPreview() {
    document.querySelector('body').classList.remove('full-screen');

    $('.g-extra').show();
    nextTick(() => {
        $('#canvas').css({ 'transform': '', 'padding-top': '', 'height': '', 'overflow-y': 'inherit' });
        $('#pages').css({ 'transform': '', 'margin-top': '' });
        $('#preview-block').css({ 'height': '' });

        if (!constants.SCALED_FULL_SCREEN_APPROACH || constants.SCALED_ZOOM_APPROACH) {
            fitToPage();
        }
        disablePrintAdvisor();
    });

    if (document.exitFullscreen) {
        document.exitFullscreen();
    } else {
        document.webkitExitFullscreen();
    }
}

function disablePrintAdvisor() {
    $('.advisor-marker').each((index, element) => {
        $(element).remove();
    });

    const printAdvisor = {
        ...store.getState().printAdvisor
    };

    printAdvisor.allowPrint = false;
    printAdvisor.enabled = false;
    store.dispatch(actions.updatePrintAdvisor(printAdvisor));
}

const enablePrintAdvisor = (props) => {

    const printAdvisor = {
        ...store.getState().printAdvisor
    }

    if (printAdvisor.addToCartURL == null) {
        //reset to step 1, othewise let the user continue from where he left off.
        printAdvisor.step = 1;
    }

    if (typeof (props) !== 'undefined') {
        printAdvisor.addToCartURL = props.addToCartUrl;
        printAdvisor.isProductInCart = props.isProductInCart;
    }

    printAdvisor.enabled = true;
    store.dispatch(actions.updatePrintAdvisor(printAdvisor));
};

const showPrintAdvisorHints = (size) => {
    //for each page add a div large as the page that has a border identifying the limits
    let bleed = inchToPixel(0.125);
    const border = 2;

    if (size === 'sandwich-board') {
        bleed = inchToPixel(0.25);
    }
    if (size === 'sandwich-board') {
        bleed = inchToPixel(0.25);
    }

    let widthPx = (2 * bleed - border);
    let heightPx = (2 * bleed - border);
    let leftPx = bleed;
    let topPx = (bleed - border);

    if (size.indexOf('sticker') > -1 || size.indexOf('label') > -1) {
        bleed = inchToPixel(0.125 / 2);

        widthPx = 5.5;
        heightPx = 4.5;
        leftPx = 3.5;
        topPx = 2.5;

    }

    //cleanup
    $('.advisor-marker').each((index, element) => {
        $(element).remove();
    })

    if (!store.getState().printAdvisor.enabled) {
        return;
    }

    $('.page').each((index, element) => {
        const $element = $(element);
        const $advisorMarker = $('<div><div/>');
        $advisorMarker.addClass('advisor-marker');
        $element.append($advisorMarker)
    })

    if (store.getState().printAdvisor.step === 1) {
        $('.advisor-marker').each((index, element) => {
            let $advisorMarker = $(element);
            $advisorMarker.css({
                position: 'absolute',
                border: 'solid ' + border + 'px rgba(252,95,87,0.75)',
                width: 'calc(100% - ' + widthPx + 'px)',
                height: 'calc(100% - ' + heightPx + 'px)',
                top: topPx + 'px',
                left: leftPx + 'px',
                zIndex: '1'
            })
        })
    }

    if (store.getState().printAdvisor.step === 2) {
        $('.advisor-marker').each((index, element) => {
            let $advisorMarker = $(element);
            $advisorMarker.css({
                position: 'absolute',
                border: 'solid ' + (2 * bleed) + 'px rgba(252,95,87,0.75)',
                width: '100%',
                height: '100%',
                top: '0px',
                left: '0px',
                zIndex: '1'
            })
        })
    }

};

const positionPrintAdvisor = () => {
    const pageBB = $('#page-1').get(0).getBoundingClientRect();
    $('.pa-advisor').first().css({
        //right : 'unset',
        //left : (pageBB.left + pageBB.width + 10) + 'px',
        //top : (pageBB.top + 80) + 'px'
    })
};

const isUSCountry = (country) => country.indexOf('United States') > -1;

const getFirstVisiblePage = () => {
    let visiblePageId = null;
    let closestToZero = 100000;
    $('.page').each((index, element) => {
        var rect = element.getBoundingClientRect();
        if (Math.abs(rect.top) < closestToZero) {
            closestToZero = Math.abs(rect.top);
            visiblePageId = element.id;
        }
    });
    return visiblePageId;
};

function getRgba(background) {
    return background
        .replace('rgba(', '')
        .replace(')', '')
        .split(',');
}

function getRgb(color) {
    return color
        .replace('rgb(', '')
        .replace(')', '')
        .split(',');
}

function getQRCodeCanvas(link, size = 500) {
    $('#qrcode canvas').remove();
    $('#qrcode').qrcode({ width: size, height: size, text: link });

    const $canvas = $('#qrcode canvas');
    const canvas = $canvas.get(0);
    const ctx = canvas.getContext('2d');
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    // add white border to the code
    tempCanvas.width = size * 1.1;
    tempCanvas.height = size * 1.1;
    tempCtx.fillStyle = 'white';
    tempCtx.fillRect(0, 0, size * 1.1, size * 1.1);
    tempCtx.drawImage(canvas, size * .05, size * .05);
    canvas.width = size * 1.1;
    canvas.height = size * 1.1;
    ctx.drawImage(tempCanvas, 0, 0);

    return $canvas;
}

function getQRCodeImage(link, size = 500) {
    const $canvas = getQRCodeCanvas(link, size);

    const data = $canvas.get(0).toDataURL("image/png");
    const img = document.createElement('img');
    img.src = data;

    const $img = $(img);
    $img.addClass('img');
    $img.width('100%')
    $img.height('100%')

    return $img;
}

export function getQRCodeImgSrc(link, size = 500) {
    const $canvas = getQRCodeCanvas(link, size);
    const data = $canvas.get(0).toDataURL("image/png");
    return data;
}

export function convertQrObjects() {
    let qrObjects = $('#pages').find('.image.qr');
    let oldQrObjects = $('#pages').find('.image img[src*="data:image"]');
    let allQrObjects = $.merge(qrObjects, oldQrObjects);
    if (allQrObjects.length > 0) {
        allQrObjects.each(function () {
            let obj = $(this).is('img') ? $(this).parent() : $(this);
            obj.addClass('qr');
            obj.removeClass('image');
            obj.attr('data-component', 'qr-code');
            obj.attr('data-ratio', '1');
            if (obj.attr('data-custom-ratio')) {
                obj.attr('data-custom-ratio', '1');
            }
        });
    }
}

export function syncUploadedImagesAltTextOnCanvas(uploadedImages) {
    const $frameImgs = $('#pages').find('.frame[data-component="frame"]');
    $frameImgs.each(function () {
        const $thisImg = $(this).find('.img');
        uploadedImages.forEach(img => {
            if (img.altText) {
                const url = $thisImg.css('background').split('"')[1];
                if (url.includes(img.path)) {
                    $thisImg.attr('aria-label', img.altText);
                    $thisImg.attr('role', 'img');
                }
            }
        })
    });
}

export function syncUploadedIconsAltTextOnCanvas(uploadedIcons) {
    const $menuObjectImgs = $('#pages').find('.menu-object[data-component="menu-object"] .icon img');
    $menuObjectImgs.each(function () {
        const $this = $(this);
        uploadedIcons.forEach(icon => {
            if (icon.altText) {
                if (icon.path === $this.attr('icon-key')) {
                    $this.attr('alt', icon.altText);
                }
            }
        })
    });
}

export function addBlankAltText() {
    const $frameImgs = $('#pages').find('.frame[data-component="frame"]');
    $frameImgs.each(function () {
        const $this = $(this);
        if (!$this.find('.img').attr('aria-label')) {
            $this.find('.img').attr('aria-label', '');
            $this.find('.img').attr('role', 'img');
        }
    });
    const $illustrations = $('#pages').find('.image[data-component="illustration"]');
    $illustrations.each(function () {
        const $this = $(this);
        if (!$this.find('svg').attr('aria-label')) {
            $this.find('svg').attr('aria-label', '');
            $this.find('.img').attr('role', 'img');
        }
    });
    const $imgs = $('#pages').find('.image[data-component="image"]');
    $imgs.each(function () {
        const $this = $(this);
        if (!$this.find('img').attr('alt')) {
            $this.find('img').attr('alt', '');
        }
    });
    const $menuObjectImgs = $('#pages').find('.menu-object[data-component="menu-object"] .icon img');
    $menuObjectImgs.each(function () {
        const $this = $(this);
        if (!$this.attr('alt')) {
            $this.attr('alt', '');
        }
    });
}

export function convertMenuObjects() {
    const $menuObjects = $('#pages').find('.menu-object[data-component="menu-object"]');
    $menuObjects.each(function () {
        const $this = $(this);
        if ($this.find('.mo-content').length) return;
        const $clone = $this.find('.items').clone();
        $this.find('.items').remove();
        let $moContent = $("<div class='mo-content'></div>");
        $moContent.append($clone);

        const $wrapper = $this.find('.wrapper');
        if ($wrapper.length) {
            $wrapper.append($moContent);
        } else {
            let $newWrapper = $("<div class='wrapper'></div>");
            $newWrapper.append($moContent);
            $this.append($newWrapper);
        }

        const styles = MenuObject.getStyles($this);
        if (styles.items) {
            styles['mo-content'] = styles.items;
            delete styles['items'];
            overwriteStyles($this, styles);
        }
    })
}

export function updateImgPathsToS3() {
    const allObjs = $.merge($('#pages').find('[data-component="image"]'), $('#pages').find('[data-component="frame"]'));
    const objsToConvert = []
    allObjs.each(function () {
        let $this = $(this);
        let url = $this.find('img').attr('src') ? $this.find('img').attr('src') :
            ($this.find('.img').css('background-image') ? $this.find('.img').css('background-image') : '');
        if (url.includes('iserve')) {
            objsToConvert.push($this);
        }
    });

    if (objsToConvert.length > 0) {
        imageService.getFreeImages().then((resp) => {
            let images = {}
            resp.data.map((it) => {
                images[it.assetId] = it.s3Path;
            })
            objsToConvert.forEach(function (obj) {
                let src = obj.find('img').attr('src') ? obj.find('img').attr('src') :
                    (obj.find('.img').css('background-image') ? obj.find('.img').css('background-image') : '');
                let assetId = src.substring(src.indexOf("assetId") + 8, src.indexOf("assetId") + 14);
                if (obj.attr('data-component') === 'image') {
                    obj.find('img').attr('src', constants.SECURE_S3_PATH + images[assetId]);
                } else {
                    obj.find('.img').css('background-image', 'url("' + constants.SECURE_S3_PATH + images[assetId] + '")');
                }
            });
        })
    }
}

function getMultiSelectType($elements) {
    const types = $elements.map($it => $it.attr('data-component'));
    return types.every((val, i, arr) => val === arr[0]) ? types[0] : 'none';
}

export function getDesignThumb(id, memberId) {
    return (isTestUrl() ? constants.CDN_TIMBER_PATH_TEST : constants.CDN_TIMBER_PATH) + memberId + "/thumb/" + id + "-w300-p1.jpg";
}

export function getDesignSizeLabel(props) {
    let label = getSizeLabel(props.designSize);
    if (label === "Table Tent" && props.designSize.indexOf("insert") > -1) {
        label = "Tabletop Insert";
    }
    return label;
}

export function getDesignDimensions(props) {
    const current = getSize(props.designSize);
    if (!current) {
        return { width: 0, height: 0, unit: 'in' };
    }

    let width = current.widthNoBleed;
    let height = current.heightNoBleed;
    let unit = current.unit;

    if (props.isCustomSize) {
        width = props.designCustomWidth;
        height = props.designCustomHeight;
        unit = props.designUnit;

        if (props.designUnit === 'in') {
            width -= 0.25;
            height -= 0.25;
        }
    }

    return { width, height, unit };
}

export function copyText(text, e, changeText = true) {
    let input = document.createElement('input');
    input.setAttribute('value', text);
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);

    const $parent = $(e.target).parent();
    if ($parent.attr('id') === 'copyLink' || $parent.attr('id') === 'copyEmbed') {
        $parent.attr('data-hint', 'Copied!');
    }

    if (changeText) {
        const $target = $(e.target);
        if ($target.hasClass('link-btn') || $target.hasClass('show-copy-confirmation')) {
            $target.text('Copied!');

            setTimeout(function () {
                $target.text('Copy');
            }, 4000);
        }
    }

    e.stopPropagation();
}

export function getOptisignLink(designId) {
    return getShortDomain() + (isTestEnv ? '/lpv2' : '') + '/design/optisign/' + designId;
}
export function getShareLink(designId) {
    return getShortDomain() + (isTestEnv ? '/lpv2' : '') + '/design/' + designId;

    // let token = websiteToken;
    // if (websiteToken.data) {
    //     token = websiteToken.data.id;
    // }
    // return 'https://' + window.location.hostname + '/menu/viewMenu.do?token=' + token + '&lite=true'
}

export function getDPLink(designId, page) {
    const pageParam = page ? `?page=${page}` : '';
    return getShortDomain() + (isTestEnv ? '/lpv2' : '') + '/dp/' + designId + pageParam;
}

export function getTelemetryLink(designId) {
    return getShortDomain() + (isTestEnv ? '/lpv2' : '') + '/dp/telemetry/' + designId;
}

function getShortDomain() {
    if (constants.isLocalhost) return 'http://localhost:8091';
    if (constants.isTestEnv) return 'https://test.mhmfun.com';
    if (constants.isStageEnv) return 'https://stage.ohbz.com';
    return 'https://ohbz.com';
}

export function getShortUrl(id) {
    return getShortDomain() + (isTestEnv ? '/lpv2' : '') + '/' + id;
}

export function getQRCodeDynamicUrl(id) {
    return getShortDomain() + (isTestEnv ? '/lpv2' : '') + '/qr/' + id;
}

export function getLandingPageDynamicShortUrl(id) {
    return getShortDomain() + (isTestEnv ? '/lpv2' : '') + '/lp/' + id;
}

export function getLPDashboardLink(locationId, landingPageId) {
    const path = landingPageId === 'new' ? 'links' : landingPageId + '/settings/status';
    const tkn = window.localStorage.getItem('tkn');
    if (constants.isTestEnv) {
        return `https://test.mhmfun.com/lpv2/app/${landingPageId}/${path}?businessId=${locationId}&tkn=${tkn}`;
    } else if (constants.isStageEnv) {
        return `https://stage.ohbz.com/app/${landingPageId}/${path}?businessId=${locationId}&tkn=${tkn}`;
    } else {
        return `https://ohbz.com/lp/app/${landingPageId}/${path}?businessId=${locationId}&tkn=${tkn}`;
    }
}

export function getLPDashboardLinkFromDesign(locationId, landingPageId, designId) {
    const tkn = window.localStorage.getItem('tkn');
    if (constants.isTestEnv) {
        return `https://test.mhmfun.com/lpv2/app/${landingPageId}/links?businessId=${locationId}&tkn=${tkn}&link=${designId}`;
    } else if (constants.isStageEnv) {
        return `https://stage.ohbz.com/app/${landingPageId}/links?businessId=${locationId}&tkn=${tkn}&link=${designId}`;
    } else {
        return `https://ohbz.com/lp/app/${landingPageId}/links?businessId=${locationId}&tkn=${tkn}&link=${designId}`;
    }
}

export function getLPMainAppLink() {
    if (constants.isStageEnv) {
        return `https://staging.mhmfun.com/home-stage/link-pages`;
    }
    return MyMenusPath + '/link-pages';
}

export function getOMDashboardLink(locationId) {
    let prefix = '/oo/app';

    if (constants.isStageEnv) {
        prefix = '/oostage/app';
    }

    return prefix + '/settings/status?businessId=' + locationId + '&tkn=' + window.localStorage.getItem('tkn');
}

export function getOMLink(url) {
    let prefix = '/oo/';

    if (isLocalhost()) {
        prefix = 'http://test.mhmfun.com/oo/';
    }

    if (constants.isStageEnv) {
        prefix = '/oostage/';
    }

    return prefix + url;
}

export function getHomeLink() {
    let folderParam = "";
    const folders = store.getState().designFolders;
    if (folders) {
        folderParam = "?folderId=" + folders;
    }
    if (constants.isStageEnv) {
        return `https://staging.mhmfun.com${MyMenusPath}/designs/${folderParam}`;
    } else {
        return `${MyMenusPath}/designs/${folderParam}`;
    }
}

export function getPrintCartLink() {
    if (constants.isStageEnv) {
        return `https://staging.mhmfun.com${MyMenusPath}/print/checkout`;
    } else {
        return `${MyMenusPath}/print/checkout`;
    }
}

export function getMaxKeyInObject(obj) {
    return Object.keys(obj).reduce(function (a, b) { return obj[a] > obj[b] ? a : b });
}

export function roundDown32(num) {
    return Math.floor(num / 32) * 32;
}

export function usePreview() {
    const { designId, designSize, revisionLabel, hasVideo } = store.getState();

    showConfirmRevisionRestoreModal(revisionLabel, () => {
        Renderer.useRevision();
        $('[data-component="menu-object"').each(function () {
            const $this = $(this);
            MenuObject.refreshStyles($this);
        })

        nextTick(() => {
            designService.updateDesign({
                id: designId,
                html: Renderer.getPagesContent(),
                size: designSize,
                hasVideo: hasVideo
            }).then(() => {
                window.location.reload();
            });
        });
    });
}

export function showPrintModal(doSave = true) {
    if (doSave) {
        store.dispatch(actions.setIsSaved(false));
        store.dispatch(actions.setIsSaving(true));
        store.dispatch(actions.markAsModified());
    }

    $('#papersSelect').val('');
    $('.pq-overlay').removeClass('pq-hidden');
    $('#pq-overlay-spinner').removeClass('pq-hidden');
    $('#pq-quote-spinner').removeClass('pq-hidden');
    $('#print-quote-calculator').removeClass('pq-hidden');

    const { designId, designSize, designName, hasVideo } = store.getState();

    if (doSave) {
        designService.updateDesign({
            id: designId,
            html: Renderer.getPagesContent(),
            size: designSize,
            name: designName,
            hasVideo: hasVideo
        }).then(() => {
            store.dispatch(actions.setIsSaved(true));
            store.dispatch(actions.setIsSaving(false));
            store.dispatch(actions.setShowExitWarning(false));
        });
    }

    if (!store.getState().isMobileVersion) {
        print.PrintQuote.Controller.start(
            { design_id: designId },
            {
                onAddToCart: () => {
                    if (!constants.SCALED_FULL_SCREEN_APPROACH) {
                        Renderer.setZoom(70);
                    }
                    fullScreenPreview({ fromPA: true });
                },
                beforeAddToCart: (addToCartOptions) => {
                    enablePrintAdvisor(addToCartOptions);
                }
            }
        );
    } else {
        print.PrintQuote.Controller.start({ design_id: designId });
    }
}

function validateFileSize(files, maxSize) {
    let isValid = true;
    const max = maxSize ? maxSize * 1024 * 1024 : 5 * 1024 * 1024; // maxSize or 5MB
    if (files && files[0].size > max) {
        isValid = false;
    }
    return isValid;
}

export function forceHttps() {
    if (!!window.data.props.https &&
        window.location.protocol.indexOf('https') === -1 &&
        window.location.hostname.indexOf('localhost') === -1) {
        window.location.href = window.location.href.replace('http', 'https');
    }
}

export function trailingZero(value) {
    return (`${value}`.length < 2) ? `0${value}` : value;
}

export function removeMenuObjectPlaceholders() {
    $(".menu-object-placeholder").remove();
    $(".menu-object-apply-placeholder").remove();
    $(".page-menu-object-placeholder").remove();
    $(".menu-object").removeClass('placeholder-mode');
}

function deselectMenuObject() {
    removeMenuObjectPlaceholders();
    store.dispatch(actions.setSelectionType('text'));
    Renderer.deselectAll();
    store.dispatch(actions.deselect());
}

function deleteEmptyMenuObject(menuObjects) {
    menuObjects.each(function () {
        const $menuObject = $(this);

        // const menuItems = getMenuItems($menuObject);
        // if (menuItems.length == 1 && JSON.stringify(menuItems[0]) === JSON.stringify(new MenuObject.MenuItem()) && !$menuObject.hasClass('in-focus')) {
        //     MenuObject.updateContent($menuObject, []);
        // }

        if ($menuObject.hasClass('in-focus')) {
            MenuObject.updateContent($menuObject, []);
        }
    });
}

function highlightAsideQrs() {
    const asideQrs = $('.aside-qr-codes').find('.saved-qr-code');
    if (store.getState().view === 'qr-codes' && asideQrs.length > 0) {
        const qrsOnCanvas = $('#pages').find('.qr');
        const qrsSelected = $('#pages').find('.qr.in-focus');
        let scrollTo = -1;
        if (qrsOnCanvas.length === 1) {
            asideQrs.each(function () {
                const $this = $(this);
                if ($this.find('img').attr('src') === qrsOnCanvas.find('img').attr('src')) {
                    $this.addClass('active');
                    scrollTo = $this.offset().top - $this.closest('.qr-code-link-images').offset().top;
                } else {
                    $this.removeClass('active');
                }
            });
        } else if (qrsSelected.length > 0) {
            const qrLinks = qrsSelected.map(function () { return $(this).find('img').attr('src'); });
            asideQrs.each(function () {
                const $this = $(this);
                if ($.inArray($this.find('img').attr('src'), qrLinks) >= 0) {
                    $this.addClass('active');
                    if (scrollTo === -1) {
                        scrollTo = $this.offset().top - $this.closest('.qr-code-link-images').offset().top;
                    }
                } else {
                    $this.removeClass('active');
                }
            });
        } else {
            asideQrs.each(function () {
                $(this).removeClass('active');
            });
        }

        if (scrollTo !== -1) {
            const scroller = $('.aside-qr-codes .scroller');
            if (!(scroller.scrollTop() < scrollTo && scrollTo < scroller.scrollTop() + 612)) {
                $('.aside-qr-codes .scroller').scrollTop(scrollTo);
            }
        }
    }
}

function getLocationIdFromStorage() {
    let locationId = window.localStorage.getItem("activeLocation") || "";
    if (locationId === "undefined") locationId = "";
    return locationId;
}

function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

export function getStoreColors(design) {
    let colors = [];
    if (design.colors) {
        colors = design.colors.indexOf(',') > -1 ? design.colors.split(',') : [design.colors];
    }
    return colors.filter((color) => !!color && color !== '#undefined' && color !== '#');
}

export function getStoreFonts(design) {
    let fonts = [];
    if (design.fonts) {
        fonts = design.fonts.indexOf(',') > -1 ? design.fonts.split(',') : [design.fonts];
    }

    const allFonts = buildFontsArray(fonts, getDesignFonts());

    if (allFonts.length !== fonts.length) {
        designService.updateDesignFonts(store.getState().designId, allFonts.join(','));
    }

    return allFonts;
}

function removeSsoKeyFromUrl(href) {
    const bits = href.split("&");
    let found = "";
    bits.forEach(bit => {
        if (bit.indexOf("key=") > -1) {
            found = bit;
        }
    });

    if (!!found) {
        let url = href.split('.com')[1];
        url = url.replace("&" + found, "");
        if (window.history) {
            window.history.replaceState({}, '', url);
        }
    }
}

export function initUrl(id) {
    if (window.location.href.indexOf("size=")) {
        Renderer.setSafeZone(false);
    }

    if (window.location.href.indexOf(id) > -1) {
        removeSsoKeyFromUrl(window.location.href);
        return;
    }

    if (window.history) {
        window.history.replaceState({}, '', `${constants.appPrefix}/design?id=${id}`);
    }
}

export function isValidString(s) {
    return s !== null && typeof s === 'string' && s !== 'undefined' && s !== 'null' && s !== 'NaN';
}

export function getEmptyPrice() {
    return { size: '', price: '', displayPrice: '' };
}

function checkScroll(scroller) {
    if (scroller) {
        const leftArrow = scroller.parent().find('.scroll-left');
        const rightArrow = scroller.parent().find('.scroll-right');

        if (scroller.scrollLeft() <= 1) {
            leftArrow.hide()
        } else {
            leftArrow.show();
        }

        if (Math.ceil(scroller.scrollLeft() + scroller.width()) >= scroller[0].scrollWidth) {
            rightArrow.hide();
        } else {
            rightArrow.show();
        }
    }
}

function scrollLeft(ev) {
    const $this = $(ev.target);
    const toScroll = $this.closest('.folders-container').find('.folders');
    toScroll.scrollLeft(toScroll.scrollLeft() - 250);
    setTimeout(() => { checkScroll(toScroll) }, 500);
}

function scrollRight(ev) {
    const $this = $(ev.target);
    const toScroll = $this.closest('.folders-container').find('.folders');
    toScroll.scrollLeft(toScroll.scrollLeft() + 250);
    setTimeout(() => { checkScroll(toScroll) }, 500);
}


export function toLogIn() {
    window.location.href = LOGIN_PATH;
}

export function getComponentFromPageBar($target) {
    let $component = $target;
    if ($target.hasClass('page-bar')) {
        const id = $target.attr('id');
        $component = $target.closest('.page').find(`.content div#${id}[data-component="${$target.attr('page-component')}"]`);
    } else if ($target.hasClass('moveable-area')) {
        $component = $(`#${Interactable.getIdFromMovableArea($target)}`);
    }
    return $component;
}

export function getSvgPath($element) {
    if (!$element) return;
    const shapeType = $element.attr('data-shape');
    return shapeType === 'circle' ? $element.find('svg ellipse') :
        shapeType === 'square' ? $element.find('svg rect') :
            $element.find('svg line');
}

function getCssFromColorData(colorDataString) {
    const colorData = JSON.parse(colorDataString);
    let css = '';
    if (colorData.mode === 'no-fill') {
        css = '#ffffff00';
    } else if (colorData.mode === 'solid') {
        css = colorData.colors[0];
    } else if (colorData.mode === 'gradient-horizontal') {
        css = `linear-gradient(180deg, ${colorData.colors[0]}, ${colorData.colors[1]})`;
    } else if (colorData.mode === 'gradient-vertical') {
        css = `linear-gradient(90deg, ${colorData.colors[0]}, ${colorData.colors[1]})`;
    } else if (colorData.mode === 'gradient-radial') {
        css = `radial-gradient(${colorData.colors[0]}, ${colorData.colors[1]})`;
    }
    return css;
}

function getBorderElement($element) {
    if (!$element) return;
    return isVideoFrame($element) ? $element.find('video') :
        isFrame($element) ? $element.find('.img') :
            MenuObject.isMenuObject($element) ? $element.find('.mo-content') :
                $element.find('img');
}

function getMOBorderColor($element = null) {
    if ($element) {
        const styles = MenuObject.getStyles($element);
        if (styles && styles['mo-content'] && styles['mo-content']['border']) {
            return styles['mo-content']['border'].split(' ')[2];
        }
    }
    return getFirstBrandColorOrDefault();
}

function getMONameBorderColor($element = null, type) {
    if ($element) {
        const styles = MenuObject.getStyles($element);
        if (styles && styles['mo-name'] && styles['mo-name'][type]) {
            const split = styles['mo-name'][type].split(' solid ');
            if (split.length > 1) {
                return split[1];
            } else {
                return '#000';
            }
        }
    }
    return getFirstBrandColorOrDefault();
}

function getMONotesLineColor($element = null) {
    if ($element) {
        const styles = MenuObject.getStyles($element);
        if (styles && styles['mo-notes'] && styles['mo-notes']['border-bottom']) {
            const split = styles['mo-notes']['border-bottom'].split(' solid ');
            if (split.length > 1) {
                return split[1];
            } else {
                return '#000';
            }
        }
    }
    return getFirstBrandColorOrDefault();
}

function getFirstBrandColorOrDefault() {
    const brandColors = window.data.member.brandColors;
    if (brandColors && brandColors.length > 0) {
        return brandColors[0].colorCode;
    } else {
        return '#000';
    }
}

function openColorPicker(type, toggle = true) {
    if (toggle && store.getState().isMobileVersion) {
        store.dispatch(actions.setIsMobileAside(true));
    }
    store.dispatch(actions.setPrevAsideView(store.getState().view));
    store.dispatch(actions.setColorPickerType(type));
    store.dispatch(actions.goToView('color-picker'));
    store.dispatch(actions.setIsLeftMenuVisible(true));
}

function openWordArt() {
    store.dispatch(actions.goToView('word-art'));
    store.dispatch(actions.setIsLeftMenuVisible(true));
}

function openAnimations() {
    store.dispatch(actions.goToView('animations'));
    store.dispatch(actions.setIsLeftMenuVisible(true));
}

export function getValidUrl(url) {
    let newUrl = window.decodeURIComponent(url);
    newUrl = newUrl.trim().replace(/\s/g, "");

    if (/^(:\/\/)/.test(newUrl)) {
        return `http${newUrl}`;
    }
    if (!/^(f|ht)tps?:\/\//i.test(newUrl)) {
        return `http://${newUrl}`;
    }

    return newUrl;
};

export function getElementScale($element) {
    const translate = $element.css('transform');
    const matrix = new DOMMatrix(translate).toJSON();

    return matrix.a;
}

export function isCornerEdge(edge) {
    return edge.indexOf('-') > -1;
}

export function roundOneDecimal(number) {
    return Math.round(number * 10) / 10;
}

export function roundTwoDecimals(number) {
    return (Math.round(number * 100) / 100).toFixed(2);
}

//https://webaim.org/resources/contrastchecker/
export function getContrast(b, f) {
    let ratio = 10;
    try {
        function getRGB(c) {
            try {
                c = parseInt(c, 16);
            } catch (err) {
                c = false;
            }
            return c;
        }

        function getsRGB(c) {
            c = getRGB(c) / 255;
            c = (c <= 0.03928) ? c / 12.92 : Math.pow(((c + 0.055) / 1.055), 2.4);
            return c;
        }

        function getL(c) {
            return (0.2126 * getsRGB(c.substr(1, 2)) + 0.7152 * getsRGB(c.substr(3, 2)) + 0.0722 * getsRGB(c.substr(-2)));
        }

        const L1 = getL(f), L2 = getL(b);
        ratio = (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
    } catch (e) {
        console.error(e);
    }
    return ratio;
}

export function cleanDots(name) {
    if (!name) return;
    if (name.endsWith('.') || (name.match(/\./g) || []).length > 1) {
        while (name.endsWith('.') || name.endsWith(' ')) {
            name = name.substring(0, name.length - 1);
        }
    }
    return name;
}

const throttle = (func, limit) => {
    let lastFunc
    let lastRan
    return function () {
        const context = this
        const args = arguments
        if (!lastRan) {
            func.apply(context, args)
            lastRan = Date.now()
        } else {
            clearTimeout(lastFunc)
            lastFunc = setTimeout(function () {
                if ((Date.now() - lastRan) >= limit) {
                    func.apply(context, args)
                    lastRan = Date.now()
                }
            }, limit - (Date.now() - lastRan))
        }
    }
}

function getPaperTypeDescription(value, designSize) {
    const desc = { value };

    switch (value) {
        case 'cardstock':
            desc.title = 'Cardstock';
            desc.description = '12pt gloss finish. Sturdy, bright and vivid colors.';

            if (isTableTent(designSize)) {
                desc.description = '12pt gloss finish - Sturdy, scored for fast assembly - self-standing'
            } else if (isTableTentInsert(designSize)) {
                desc.description = '12pt gloss finish - Sturdy, excellent color definition.';
            } else if (isBusinessCard(designSize)) {
                desc.description = '16pt gloss finish - Thick and high-quality.';
            } else if (isLoyaltyCard(designSize)) {
                desc.description = '14pt gloss finish - Premium. High-quality.';
            }
            break;

        case 'laminated':
            desc.title = 'Laminated Cardstock';
            desc.description = '12pt gloss finish with 5mil lamination. Easy to clean!';

            if (isTableTentInsert(designSize)) {
                desc.description = '12pt gloss finish with 5 mil lamination - High-quality laminate enhances durability.';
            }
            break;

        case 'synthetic':
            desc.title = 'Synthetic Paper';
            desc.description = 'Premium 14pt. Durable, water and tear-resistant, feels like paper.';
            break;

        case 'single-use':
            desc.title = 'Single-Use Paper';
            desc.description = '50lb Offset. Lightweight, large quantity, recyclable.';
            break;

        case 'double-thick-cardstock':
            desc.title = 'Double Thick Cardstock';
            desc.description = '34pt matte finish, double down on a great first impression';
            break;

        case 'gloss-finish':
            desc.title = 'Gloss Finish';
            desc.description = '100lb gloss finish - Excellent color definition.';

            if (isFoldedMenu(designSize) || isTakeout(designSize)) {
                desc.description = '100lb paper - Bright and vivid colors, comes folded. Best suited for takeout menus.';
            } else if (isMenuMailer(designSize)) {
                desc.description = '100lb paper - Bright and vivid colors, comes folded.';
            }
            break;

        case 'matte-finish':
            desc.title = 'Matte Finish';
            desc.description = '60lb Offset - Crayon-friendly.';

            if (isLabel(designSize)) {
                desc.description = 'Permanent adhesive';
            }
            break;

        case 'white-gloss':
            desc.title = 'White Gloss Finish';
            desc.description = 'Permanent adhesive';
            break;

        case 'sign-only':
            desc.title = 'Sign Insert Only';
            desc.description = '4mm white corrugated plastic. Sized for 22”x28” Holder';
            break;

        case 'a-frame':
            desc.title = 'A-Frame Holder and 2 Signs';
            desc.description = 'Lightweight plastic A-Frame includes two 22”x28” sign inserts';
            break;

        case 'satin-finish':
            desc.title = 'Satin Finish';
            desc.description = 'Vivid colors without the glare.';
            break;

        default:
            desc.title = value;
            desc.description = '';
            break;
    }

    return desc
}

function iniFrame() {
    return window.self !== window.top;
}

function getPaginationDescription(value, designSize) {
    const desc = { value };

    switch (value) {
        case 'single-sided':
            desc.title = 'Single-sided';
            desc.description = 'One side of the sheet is printed, the other side is blank.';
            if (isTableTentInsert(designSize)) {
                desc.title = 'Single-sided insert';
            }
            break;

        case 'double-sided':
            desc.title = 'Double-sided';
            desc.description = 'Both sides of the sheet are printed, page one on front, page two on back.';
            if (isTableTentInsert(designSize)) {
                desc.title = 'Double-sided insert';
            }
            break;

        case 'front-double-sided':
            desc.title = 'First Page + Double-sided Interior';
            desc.description = 'First page printed single-sided, interior pages printed double-sided.';
            break;

        case 'back-double-sided':
            desc.title = 'Last Page + Double-sided Interior';
            desc.description = 'Last page printed single-sided, interior pages printed double-sided.';
            break;

        case 'front-back-double-sided':
            desc.title = 'First & Last Page + Double-sided Interior';
            desc.description = 'First and last pages printed single-sided, interior pages printed double-sided.';
            break;

        default:
            desc.title = value;
            desc.description = '';
            break;
    }

    return desc;
}

export function insertAndShift(arr, from, to) {
    let cutOut = arr.splice(from, 1)[0];
    arr.splice(to, 0, cutOut);
}

export function selectMultiple(elements) {
    const len = elements.length;
    Renderer.select(elements[0]);
    store.dispatch(actions.select(elements[0]));
    setTimeout(() => {
        let newElements = elements.splice(1, len - 1);
        selectCopies(newElements);
    }, 50)

    function selectCopies(copies) {
        if (copies.length > 0) {
            copies.forEach((c) => {
                Renderer.select(c);
                store.dispatch(actions.multiSelect(c));
                setTimeout(() => {
                    let newCopies = copies.splice(1, copies.length - 1);
                    this.selectCopies(newCopies);
                }, 50);
            })
        }
    }
}

export function getFitZoom() {
    let availableHeight = $(window.top).height() - 150;
    $(window).scrollLeft(0);

    const pageSize = getActiveDimensions();
    let height = pageSize.height;

    if (pageSize.key === 'gift-certificate-landscape') {
        height = height * 1.25;
    }

    return parseInt(availableHeight / height * 100)
}

export function getFillZoom() {
    const $canvas = $('#canvas');
    $('#pages').css({ 'margin-left': '' });
    $(window).scrollLeft(0);

    let availableWidth = $canvas.outerWidth() - 60;

    const pageSize = getActiveDimensions();
    return parseInt(`${availableWidth / pageSize.width * 100}`, 10);
}

function hasVideo() {
    return $('.page video').length ? true : false;
}

function hasAnimation() {
    return $('.page .ani-wrapper').length ? true : false;
}

function getElementBrightness(element) {
    const match = element[0].style.filter.match(/brightness\((\d+(\.\d+)?)%\)/);
    return match ? parseFloat(match[1]) : 100;
}

function getElementContrast(element) {
    const match = element[0].style.filter.match(/contrast\((\d+(\.\d+)?)%\)/);
    return match ? parseFloat(match[1]) : 100;
}

function getElementVibrance(element) {
    const match = element[0].style.filter.match(/vibrance\((\d+(\.\d+)?)%\)/);
    return match ? parseFloat(match[1]) : 100;
}

function getElementSaturation(element) {
    const match = element[0].style.filter.match(/saturate\((\d+(\.\d+)?)%\)/);
    return match ? parseFloat(match[1]) : 100;
}

function getElementBlur(element) {
    const match = element[0].style.filter.match(/blur\((\d+(\.\d+)?)px\)/);
    return match ? parseFloat(match[1]) : 0;
}

function getElementGrayscale(element) {
    const match = element[0].style.filter.match(/grayscale\((\d+(\.\d+)?)\)/);
    return match ? parseFloat(match[1]) : 0;
}

function getElementSepia(element) {
    const match = element[0].style.filter.match(/sepia\((\d+(\.\d+)?)\)/);
    return match ? parseFloat(match[1]) : 0;
}

function getElementInvert(element) {
    const match = element[0].style.filter.match(/invert\((\d+(\.\d+)?)\)/);
    return match ? parseFloat(match[1]) : 0;
}

function getElementMuted(element) {
    const muted = element.attr('muted');
    return (typeof muted !== 'undefined' && muted !== false && muted !== 'false') ? true : false;
}

function getElementAnimation(element) {
    return element[0].style.animation;
}

function getVideoLongestDuration() {
    const videos = $('.page video');

    let longestDuration = 0;

    videos.each((i, video) => {
        if (video.duration >= longestDuration) {
            longestDuration = video.duration;
        }
    })

    return longestDuration > 30 ? 30 : parseInt(longestDuration);
}

function getPixabayVideoPosterUrl(video) {
    return `https://i.vimeocdn.com/video/${video.picture_id}.jpg`;
}

function getPexelsSDVideo(video) {
    let sd = null;

    video.video_files.forEach(file => {
        if (file.quality == 'sd') {
            if (!sd || (sd && sd.width > file.width)) {
                sd = file;
            }
        }

    })

    return sd;
}

function getPexelsHDVideo(video) {
    let hd = null;

    video.video_files.forEach(file => {
        if (file.quality == 'hd') {
            if (!hd || (hd && hd.width > file.width)) {
                hd = file;
            }
        }

    })

    return hd;
}

const EmailRegEx = /^[\w!#$%&'*+-/=?^`.{|}~]+@[\w-]+\.[.\w][.\w]+$/;
export function isValidEmail(email) {
    return EmailRegEx.test(email);
}

function isPixelDesign() {
    return getActiveDimensions() && getActiveDimensions().unit === 'px'
}

function createAnimation($element, type) {
    const elemId = $element[0].id;
    const x = getTranslateX($element);
    const y = getTranslateY($element);
    const originalTransform = $element[0].style.transform;

    let animations = document.getElementById(`animations_${elemId}`);
    if (animations) animations.remove();

    animations = document.createElement('style');
    animations.id = `animations_${elemId}`;
    $element.closest('.page')[0].appendChild(animations);

    switch (type) {
        case 'slide-to-bottom':
            animations.textContent += `
                @keyframes slide-to-bottom_${elemId} {
                    0% {
                        opacity: 0;
                        transform: translate(${x}px, ${y + 100}px);
                    }
                    100% {
                        opacity: 1;
                    }
                }`;
            break;

        case 'slide-to-right':
            animations.textContent += `
                @keyframes slide-to-right_${elemId} {
                    0% {
                        opacity: 0;
                        transform: translate(${x - 100}px, ${y}px);
                    }
                    100% {
                        opacity: 1;
                    }
                }`;
            break;

        case 'wipe':
            animations.textContent += `
                @keyframes wipe_${elemId} {
                    0% {
                        clip-path: polygon(0 0, 0% 100%, 0% 100%, 0 0);
                    }
                    100% {
                        clip-path: polygon(0 0, 0 100%, 100% 100%, 100% 0);
                    }
                }`;
            break;

        case 'breathe':
            animations.textContent += `
                @keyframes breathe_${elemId} {
                    0% {
                        opacity: 0;
                        transform: translate(${x}px, ${y}px) scale(0.8);
                    }
                    100% {
                        opacity: 1;
                        // transform: translate(${x}px, ${y}px) scale(1);
                    }
                }`;
            break;

        case 'pop':
            animations.textContent += `
                @keyframes pop_${elemId} {
                    0% {
                        opacity: 0;
                        transform: translate(${x}px, ${y}px) scale(0);
                    }
                    60% {
                        opacity: 1;
                        transform: translate(${x}px, ${y}px) scale(1.2);
                    }
                    100% {
                        opacity: 1;
                        // transform: translate(${x}px, ${y}px) scale(1);
                    }
                }`;
            break;
        case 'fade':
            animations.textContent += `
                @keyframes fade_${elemId} {
                    0% {
                        opacity: 0;
                    }
                    100% {
                        opacity: 1;
                    }
                }`;
            break;
    }
}

function getTranslateX($element) {
    var computedStyle = window.getComputedStyle($element[0]);
    var transformValue = computedStyle.transform;

    var matrix = transformValue.match(/^matrix\((.+)\)$/);
    if (matrix) {
        var matrixValues = matrix[1].split(', ');
        var translateX = parseFloat(matrixValues[4]);
        return translateX
    }
}

function getTranslateY($element) {
    var computedStyle = window.getComputedStyle($element[0]);
    var transformValue = computedStyle.transform;

    var matrix = transformValue.match(/^matrix\((.+)\)$/);
    if (matrix) {
        var matrixValues = matrix[1].split(', ');
        var translateY = parseFloat(matrixValues[5]);
        return translateY
    }
}

function getCookie(name) {
    const search = name + "=";
    let value = "";
    if (document.cookie.length > 0) {
        let offset = document.cookie.indexOf(search);
        if (offset !== -1) {
            offset += search.length;
            let end = document.cookie.indexOf(";", offset);
            if (end === -1) end = document.cookie.length;

            const unescape = window.unescape || window.decodeURI;
            value = unescape(document.cookie.substring(offset, end));
        }
    }
    return value;
}

function areElementsOnTheSameLine(element1, element2) {
    const rect1 = element1.getBoundingClientRect();
    const rect2 = element2.getBoundingClientRect();

    const verticalTolerance = 3;
    return Math.abs(rect1.bottom - rect2.bottom) <= verticalTolerance;
}

function compareColors(color1, color2) {
    function parseColor(color) {
        if (color === '') {
            return { r: 255, g: 255, b: 255 };
        } else if (color.startsWith('#')) {
            return hexToRgb(color);
        } else if (color.startsWith('rgb')) {
            return rgbStringToRgb(color);
        }
        return null;
    }

    function hexToRgb(hex) {
        hex = hex.replace('#', '');
        if (hex.length === 3) {
            hex = hex.split('').map(char => char + char).join('');
        }

        const bigint = parseInt(hex, 16);
        return {
            r: (bigint >> 16) & 255,
            g: (bigint >> 8) & 255,
            b: bigint & 255
        };
    }

    function rgbStringToRgb(rgbString) {
        const result = rgbString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
        if (!result) {
            return null;
        }

        return {
            r: parseInt(result[1], 10),
            g: parseInt(result[2], 10),
            b: parseInt(result[3], 10)
        };
    }

    const rgb1 = parseColor(color1);
    const rgb2 = parseColor(color2);

    if (!rgb1 || !rgb2) {
        return false;
    }

    return rgb1.r === rgb2.r && rgb1.g === rgb2.g && rgb1.b === rgb2.b;
}

function isSizeLandscape(size) {
    return size.width > size.height
}

function isSizePortrait(size) {
    return size.width < size.height
}

function isSizeSquare(size) {
    return size.width = size.height
}

function getUploadedImgSrc(image) {
    if (image.path.indexOf('imageservice') > -1) {
        return window.location.origin + image.path;
    } else if (image.source === 'oo') {
        return 'https://mhm-oo.s3.amazonaws.com/' + image.path;
    } else {
        return 'https://timber.mhmcdn.com/' + image.path;
    }
}

function getUploadedIconPath(icon) {
    if (icon.source === 'oo') {
        return `${constants.SECURE_S3_OO_PATH}${icon.path ? icon.path : icon.iconKey}`;
    } else {
        return `${constants.SECURE_S3_PATH}${icon.path ? icon.path : icon.iconKey}`;
    }
}

function resassignMenuObjectIds() {
    $('.pages').find('[data-component="menu-object"]').each(function () {
        const $moObject = $(this);
        const dataSource = $moObject.attr('data-source');
        const isToast = dataSource === 'toast';
        const isSquare = dataSource === 'square';
        const isPopmenu = dataSource === 'popmenu';
        const isTreez = dataSource === 'treez';
        const isItemLibrary = dataSource === 'itemlibrary';
        const isPos = isToast || isSquare || isTreez || isPopmenu || isItemLibrary;
        if (!isPos) {
            $moObject.attr('id', UUID.get());
            $moObject.find('.item[data-id]').each(function () {
                const $this = $(this);
                const oldId = $this.attr('data-id');
                const newId = UUID.get();
                $this.attr('data-id', newId);
                const oldConfig = $moObject.find('.config').text();
                const newConfig = oldConfig.replaceAll(oldId, newId);
                $moObject.find('.config').text(newConfig);
            });
        }
        MenuObject.refreshStyles($moObject);
    })
}

export function isILActive() {
    return (!!window.localStorage.getItem('itemLibrary') ||
      window.data.member.email.indexOf('@musthavemenus') > -1 ||
      window.data.member.email.indexOf('@itemlibrarytoast.com') > -1);
}

function onApplyToAllConfirm() {
    const $btn = $('#applyToAllConfirmBtn');
    $btn.text('Loading...');
}

export default {
    deepClone,
    getLocationIdFromStorage,
    getMultiSelectType,

    getRgba,
    getRgb,

    supportsFullscreen,
    fullScreenPreview,
    disableFullScreenPreview,

    debounce,
    throttle,
    ellipsis,
    showDesignInUseModal,
    showConfirmRevisionRestoreModal,

    showAutoSaveFailed,
    showSaveHangingModal,

    isControlKey,
    isTestUrl,
    isLocalhost,

    getNextWordBreak,
    getPrevWordBreak,

    getFriendlySize,

    getHeight,
    getWidth,

    getActiveDimensions,
    setPageDimensions,
    getDesignUnit,

    sizes,
    getSize,
    getFont,
    getFontByLabel,

    UUID,
    pixelToPercent,
    percentToPixel,
    pixelToInch,
    inchToPixel,
    inchToPercent,

    initDropdown,
    hideDropdown,

    radToDegree,

    getElementContent,
    buildFontsArray,
    getPointsInPixels,

    roundToZoomStep,

    isImage,
    isVideoFrame,
    isQrCode,
    isPage,
    isFrame,
    isFramePreview,
    isShape,
    isIllustration,
    isLine,
    isSquare,
    isCircle,
    isText,
    isWrappedText,
    isGroup,
    isBackground,
    isResizable,
    isSideResize,
    isInGroup,
    getGroup,
    isQRFrameEmpty,

    getPageInViewport,
    getPageInViewportNumber,
    bringInViewport,
    bringInViewportMobile,

    toDegrees,
    toRadians,

    getNumberOfPages,

    isA4,

    isFlyer,
    isSocialDesign,
    isBusinessCard,
    isLoyaltyCard,
    isCommentCard,
    isRackCard,
    isPortraitCard,
    isMenu,
    isKidsMenu,
    isGiftCertificate,
    isTakeout,
    isFoldedMenu,
    isHalfPageMenu,
    isFacebookPost,
    isFacebookStory,
    isInstagramPost,
    isInstagramStory,
    isTableTent,
    isTableTentInsert,
    isPoster,
    isDigitalMenuBoard,
    isMenuMailer,
    isSticker,
    isLabel,
    isSandwichBoard,
    isCustomSize,
    isLogo,

    isSelected,
    rgb2hex,
    hex2rgb,

    getFontByFamily,
    getFontByKey,

    getLongtailType,
    getPrintCartSizeLabel,
    getSizeLabel,
    getStatusLabel,
    getSizeWithLabel,
    getSizeDimensionsLabel,

    isOffPage,
    getPositionPage,
    hasDesignName,
    getCssRule,
    stripDoubleNewLine,

    nextTick,
    timeout,
    onFontsLoaded,

    isSafari,
    isFirefox,
    isEdge,
    isWindows,
    isGroupAlteringStyle,
    isLineHeightAlteringStyle,

    isContentEditable,
    setContentEditable,
    convertInlineSize,

    getComponentLineHeight,

    cleanFont,
    toCamelCase,
    asParagraph,
    formatLetterSpacing,

    getStrokeWidth,
    isNewLineChar,

    isLocked,
    isHyperlink,
    getHref,
    initIdleCheck,
    initSessionIdleCheck,
    resetIdle,
    resetSessionIdle,
    initRegCheck,
    isAuthOverlay,

    getPageNumber,
    getPageId,
    getPanelFold,

    showPrintAdvisorHints,
    enablePrintAdvisor,
    positionPrintAdvisor,
    isUSCountry,

    getFirstVisiblePage,

    initCustomFontsLoad,
    initCustomColorsLoad,
    initFavoriteFontsLoad,

    getQRCodeCanvas,
    getQRCodeImage,
    getQRCodeImgSrc,
    convertQrObjects,
    highlightAsideQrs,

    updateImgPathsToS3,

    getShortDomain,

    validateFileSize,

    deselectMenuObject,
    deleteEmptyMenuObject,

    checkScroll,
    scrollLeft,
    scrollRight,

    getComponentFromPageBar,
    getCssFromColorData,
    getBorderElement,
    getMOBorderColor,
    getMONameBorderColor,
    getMONotesLineColor,
    openColorPicker,
    openWordArt,
    openAnimations,

    getValidUrl,

    getPaperTypeDescription,
    getPaginationDescription,

    iniFrame,

    hasVideo,
    hasAnimation,

    getElementBrightness,
    getElementContrast,
    getElementVibrance,
    getElementSaturation,
    getElementBlur,
    getElementGrayscale,
    getElementSepia,
    getElementInvert,
    getElementMuted,
    getElementAnimation,
    getVideoLongestDuration,

    getPixabayVideoPosterUrl,
    getPexelsSDVideo,
    getPexelsHDVideo,

    isPixelDesign,

    createAnimation,
    getTranslateX,
    getTranslateY,

    getCookie,
    areElementsOnTheSameLine,

    compareColors,

    isSizeLandscape,
    isSizePortrait,
    isSizeSquare,

    getUploadedImgSrc,
    getUploadedIconPath,

    resassignMenuObjectIds,
    onApplyToAllConfirm,
}
